// app.jsx — root for Nurse CNE
// Modes: loading → reset → onboarding → (guest OR ready)
// Guest mode: all browse routes (home, discover, course detail, profile-prefs)
// work without a session. Auth-required actions (enroll, save, log, view record,
// view saved, view reminders) prompt an overlay sign-in via requireAuth().

// Hooks are called as `React.useX(...)` member calls so react-hooks/
// rules-of-hooks can see them (it only recognises `/^use[A-Z]/` and
// `React.useX`). Do not reintroduce destructured short aliases.

// ‼️ THE APP HAD NO WAY TO BOUND A HUNG REQUEST, and one 4s watchdog was
// silently covering for that. `DB.getProfile()` is a bare `await` on fetch,
// which has no default timeout in a browser — a hung profile query never
// settles. The auth bootstrap's session watchdog was the only thing rescuing
// it, which is why moving that watchdog earlier would have turned "4 seconds,
// wrong state" into "loading forever" (RELEASE.md §102).
//
// Resolves to `fallback` if `promise` has not settled in `ms`. The promise is
// NOT cancelled — fetch is not cancellable here — it is abandoned: a late
// resolution simply has no one listening, which is correct, because by then the
// UI has already routed on the fallback.
function settleWithin(promise, ms, fallback, onTimeout) {
  let timer = null;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => { try { onTimeout?.(); } catch (_) {} resolve(fallback); }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => { if (timer) clearTimeout(timer); });
}

// How long the profile query may take before the bootstrap gives up on it and
// routes with what it already knows. Deliberately longer than the session
// watchdog: by this point a session EXISTS, so the user is signed in and the
// only question is which screen they land on.
const PROFILE_LOAD_TIMEOUT_MS = 8000;

function NurseCNEApp() {
  const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
    "lang": "en",
    "theme": "light",
    "density": "regular",
    "discoverView": "list",
    "showWalletDetails": true
  }/*EDITMODE-END*/;

  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const lang = t.lang;
  const setLang = (v) => setTweak('lang', v);

  // First-launch language picker gate. localStorage marker is set once
  // and never cleared — language can still be changed later via
  // Profile → Preferences → App language. Initialised eagerly from the
  // marker so a returning user never sees the picker.
  const [langPicked, setLangPicked] = React.useState(() => {
    try { return localStorage.getItem('vital_lang_picked') === '1'; }
    catch (_) { return true; } // fail-open: if localStorage is broken, don't block the app
  });

  // ── Session bootstrap ─────────────────────────────────────────────────
  // authState: 'loading' (initial) | 'reset' (deep-link) | 'onboarding' | 'ready'
  // Note: there is no longer a forced 'auth' state — guests use the app with
  // a null session, and authOverlay holds the prompt state for gated actions.
  const [authState, setAuthState] = React.useState('loading');
  const [session, setSession]     = React.useState(null);
  const [profile, setProfile]     = React.useState(null);
  // True while AuthAPI.deleteAccount() is running. Drives a full-screen
  // splash overlay that blocks all interaction (Sheets, navigation,
  // background clicks) so the user can't fire a second sheet or tab
  // change while the server cascade is mid-flight. Cleared by
  // completeAccountDeletion() on success or by the error path so a
  // failed deletion drops back to a normal UI.
  const [deleting, setDeleting]   = React.useState(false);

  // Recovery flow refs. All three must be cleared explicitly by the screen
  // that owns each state — finalize() reads them to keep auth events from
  // bouncing the user back to home mid-flow.
  //
  //   recoveryRef       — true while ResetPasswordScreen owns the UI.
  //   postResetRef      — true while PasswordResetSuccessScreen owns the UI.
  //                       Set AFTER successful password update, BEFORE
  //                       signOut, so the signOut-triggered onAuthChange
  //                       doesn't drop the user onto home as a guest.
  //   recoveryErrorRef  — { code, description } when the recovery link
  //                       was invalid / expired. Dedicated state, never
  //                       falls through to home.
  const recoveryRef      = React.useRef(false);
  const postResetRef     = React.useRef(false);
  const recoveryErrorRef = React.useRef(null);

  // In-app navigation deep links (vital://log etc.) — App Store in-app
  // events and future marketing links. handleDeepLink stashes the target
  // here WITHOUT claiming the bootstrap slot (the normal session flow must
  // still run), and bumps navNonce so the consumption effect below fires
  // even when the app is already 'ready' (warm tap). Consumed exactly once,
  // only when authState === 'ready' — links tapped while locked or
  // mid-onboarding apply after the user gets through.
  const pendingNavRef = React.useRef(null);
  const [navNonce, setNavNonce] = React.useState(0);

  // lockedRef gates the entire signed-in UI when biometric login is
  // enrolled. True from the moment cold-launch detects a Keychain
  // credential until the user either passes Face ID or chooses
  // "Sign in another way". While true, finalize() routes to 'locked' and
  // refuses to expose the persisted Supabase session to the UI — even
  // if onAuthChange fires from background token refresh.
  const lockedRef = React.useRef(false);

  // Prompts Face ID, then uses the Keychain refresh token to mint a fresh
  // Supabase session. Called both by the bootstrap effect on cold launch
  // and by LockedScreen's "Try again" button.
  //
  // Resolves to:
  //   { session }                — unlocked, app should route to home
  //   { error }                  — verify failed / cancelled, stay locked
  //   { error, escape: true }    — credential is stale/mismatched, the
  //                                caller should drop to sign-in via
  //                                signInAnotherWay()
  const attemptBiometricUnlock = React.useCallback(async (_opts) => {
    const Bio = window.BiometricAPI;
    if (!Bio?.isSupported?.()) {
      return { error: { message: 'Biometric not supported on this device' }, escape: true };
    }

    // If iOS Settings has disabled Face ID for the app since we enrolled,
    // bail out cleanly rather than locking the user out forever.
    const avail = await Bio.checkAvailability();
    if (!avail.isAvailable) {
      console.warn('[Vital biometric] availability check failed, reason=', avail.reason);
      await Bio.clearCredentials();
      return { error: { message: avail.reason || 'Biometric unavailable' }, escape: true };
    }

    const verifyRes = await Bio.promptVerify('Sign in to Vital with Face ID');
    if (verifyRes.error) {
      // User cancelled or biometric failed — stay locked, retry possible.
      return { error: verifyRes.error };
    }

    const creds = await Bio.loadCredentials();
    if (!creds?.refresh_token) {
      console.warn('[Vital biometric] Keychain returned no refresh_token after verify');
      await Bio.clearCredentials();
      return { error: { message: 'No stored credentials' }, escape: true };
    }
    console.log('[Vital biometric] token restored from Keychain for userId=', creds.userId);

    // Clear the lock BEFORE any auth call so the synchronous
    // onAuthChange triggered by setSession / refreshSession routes
    // through finalize() normally instead of pinning to 'locked'.
    lockedRef.current = false;

    // Prefer the in-memory persisted session — supabase-js auto-
    // restores it from localStorage on client init and it carries the
    // most recently rotated refresh_token. The Keychain copy is only
    // used as fallback if the persisted session is missing (cleared)
    // or belongs to a different user (defence-in-depth).
    let session = null;
    let restorePath = '';
    try {
      const cur = await window.sb.auth.getSession();
      const persisted = cur?.data?.session;
      if (persisted?.user?.id === creds.userId) {
        session = persisted;
        restorePath = 'persisted';
      } else if (persisted && persisted.user.id !== creds.userId) {
        console.warn('[Vital biometric] persisted session userId mismatch — wiping');
        await Bio.clearCredentials();
        await window.WidgetAPI?.clear?.();
        try { await window.sb.auth.signOut(); } catch (_) {}
        return { error: { message: 'Account mismatch — please sign in again' }, escape: true };
      }
    } catch (e) {
      console.warn('[Vital biometric] getSession threw', e?.message || e);
    }

    if (!session) {
      console.log('[Vital biometric] no persisted session, falling back to refreshSession');
      const { data, error } = await window.sb.auth.refreshSession({ refresh_token: creds.refresh_token });
      if (error || !data?.session) {
        console.warn('[Vital biometric] refreshSession failed; wiping Keychain', error?.message);
        await Bio.clearCredentials();
        return { error: { message: error?.message || 'Session expired. Please sign in again.' }, escape: true };
      }
      if (data.session.user.id !== creds.userId) {
        console.warn('[Vital biometric] refreshSession userId mismatch — wiping');
        await Bio.clearCredentials();
        await window.WidgetAPI?.clear?.();
        try { await window.sb.auth.signOut(); } catch (_) {}
        return { error: { message: 'Account mismatch — please sign in again' }, escape: true };
      }
      session = data.session;
      restorePath = 'refreshSession';
      // refreshSession rotates the refresh_token — store the new one.
      await Bio.storeCredentials({
        userId: session.user.id,
        email: session.user.email,
        refresh_token: session.refresh_token,
      });
    }

    console.log('[Vital biometric] supabase session restored for', session.user.email, 'via', restorePath);

    // Route to home. The onAuthChange listener may have already run
    // finalize() during the auth call above; this explicit pass
    // guarantees profile load + state transition even when the
    // persisted-session branch fired no auth event at all.
    setSession(session);
    const p = await DB.getProfile(session.user.id).catch(() => null);
    setProfile(p);
    try { AuthAPI.rememberIdentity?.({ email: session.user.email, name: p?.display_name }); } catch (_) {}
    // Same routing rules as finalize(): trust the cached onboarded
    // flag when profile fetch fails. This is the offline-biometric
    // path so this branch matters in practice — without it, an
    // Airplane-Mode cold launch into Face ID dumps the user into
    // OnboardingFlow.
    //
    // ⚠ ORDER-DEPENDENT — keep wasOnboardingSkipped above (p && !p.onboarded).
    // Mirrors the chain in finalize() below. The SIWA bootstrap in
    // db.js relies on this order to satisfy App Store Review §5.1.1(iv).
    if (p?.onboarded) {
      AuthAPI.markUserOnboarded(session.user.id);
      AuthAPI.clearOnboardingSkipped?.(session.user.id);
      setAuthState('ready');
    } else if (AuthAPI.wasOnboardingSkipped?.(session.user.id)) {
      setAuthState('ready');
    } else if (p && !p.onboarded) {
      setAuthState('onboarding');
    } else if (AuthAPI.wasUserOnboarded(session.user.id)) {
      console.log('[Vital biometric] profile fetch failed but cached onboarded flag set → ready');
      setAuthState('ready');
    } else {
      setAuthState('onboarding');
    }
    return { session };
  }, []);

  // Post-login biometric enrol prompt. Triggers once per user on the
  // first sign-in that finds the device supports biometric and the user
  // hasn't yet enrolled. The "asked" flag lives in localStorage keyed
  // by userId so declining doesn't re-prompt on every launch, but a
  // different account on the same device gets its own one-time ask.
  const [enrolPromptOpen, setEnrolPromptOpen] = React.useState(false);
  const [enrolBiometryType, setEnrolBiometryType] = React.useState('faceId');
  const enrolPromptCheckedRef = React.useRef(null); // tracks which userId we last checked
  // Set to the userId on a REAL sign-in (SIGNED_IN auth event), never on a
  // cold-start session restore (INITIAL_SESSION). The enrol effect gates on
  // this so the "Enable Face ID?" sheet only appears after an actual login —
  // not every time the app is closed and reopened with a persisted session.
  const freshSignInRef = React.useRef(null);

  // Push soft-ask (pre-permission priming). { moment: 'save' | 'record' } | null.
  // Offered at most once per user, after a value moment — never at cold start.
  const [pushSoftAsk, setPushSoftAsk] = React.useState(null);
  const pushSoftAskFiredRef = React.useRef(false); // guards against a double-fire within one session
  const [handleInvite, setHandleInvite] = React.useState(null); // D2: one-time community-handle invite
  const inviteCheckedRef = React.useRef(false);
  const [whatsNewDual, setWhatsNewDual] = React.useState(null); // one-time dual-certificate announcement
  const whatsNewCheckedRef = React.useRef(false);
  const reviewFiredRef = React.useRef(false); // App Store review: one ask per session, max
  // Review triage ({} | null) + the FeedbackSheet it can hand off to. The
  // triage sheet itself never mentions ratings — see maybeOfferReview below.
  const [reviewAsk, setReviewAsk] = React.useState(null);
  const [reviewFeedbackOpen, setReviewFeedbackOpen] = React.useState(false);
  // A requested first-certificate scan waits for every higher-priority sheet.
  // This ref is intentionally not persisted: Home's durable firstScan notice
  // is the recovery path if the app closes before it can open.
  const pendingFirstScanRef = React.useRef(false);

  const onEnrolEnable = React.useCallback(async () => {
    const Bio = window.BiometricAPI;
    if (!session) { setEnrolPromptOpen(false); return; }
    const res = await Bio.enable(session);
    Bio.markPromptShownFor(session.user.id);
    setEnrolPromptOpen(false);
    if (res?.error) {
      console.warn('[Vital biometric] enrol declined or failed:', res.error.message);
      if (typeof window.showVitalToast === 'function') {
        window.showVitalToast(res.error.message || (t.lang === 'zh' ? '無法啟用' : 'Could not enable'));
      }
      return;
    }
    if (typeof window.showVitalToast === 'function') {
      const typeLabel = enrolBiometryType === 'touchId' ? 'Touch ID' : 'Face ID';
      window.showVitalToast(t.lang === 'zh' ? `${typeLabel} 已啟用` : `${typeLabel} enabled`);
    }
  }, [session, t.lang, enrolBiometryType]);

  const onEnrolDecline = React.useCallback(() => {
    if (session?.user?.id) {
      window.BiometricAPI?.markPromptShownFor?.(session.user.id);
    }
    setEnrolPromptOpen(false);
  }, [session]);

  // "Sign in another way" — explicit escape from the locked state. Per
  // the spec, we must clear the local Supabase session before routing
  // to sign-in so the user cannot get into the signed-in UI without
  // re-authenticating. Also clears the Keychain entry so a future cold
  // launch doesn't re-enter the locked state.
  const signInAnotherWay = React.useCallback(async () => {
    console.log('[Vital] user chose Sign in another way — clearing session + biometric');
    try { await window.BiometricAPI?.clearCredentials?.(); } catch (_) {}
    try { await window.sb.auth.signOut(); } catch (_) {}
    lockedRef.current = false;
    setSession(null); setProfile(null);
    setSavedIds(new Set()); setEnrolledIds(new Set()); setRecords([]); setReminders([]);
    setAuthState('ready');
    setAuthOverlay({ intent: 'signin' });
  }, []);

  // ── Course catalogue (fetched from Supabase cne_courses) ──────────────
  // Starts with the locally-bundled fallback (data.js → COURSES) so the
  // UI never renders empty. After mount we hydrate the on-device cache
  // (window.CoursesCache) for instant offline-readable content, then
  // replace it with live Supabase rows when the network succeeds.
  // `coursesVersion` is bumped to force a re-render.
  const [coursesVersion, setCoursesVersion] = React.useState(0);
  const [coursesLoading, setCoursesLoading] = React.useState(true);
  const [coursesError,   setCoursesError]   = React.useState(null);

  // Offline / fetch-status state — together they drive the banner.
  // isOffline: from @capacitor/network (or navigator.onLine on web).
  // lastCourseFetchFailed: true if the most recent fetchCourses returned
  // an error OR threw. Cleared on a successful fetch. Acts as a second
  // signal for "we can't actually reach the API" in case the OS says
  // connected but a captive portal / DNS block is in the way.
  // coursesShowingCache: true while the UI is showing cached data
  // (i.e. a live fetch hasn't yet replaced it). Used so we don't flash
  // the banner just because a fetch is in-flight on a working network.
  const [isOffline, setIsOffline] = React.useState(false);
  const [lastCourseFetchFailed, setLastCourseFetchFailed] = React.useState(false);
  const [coursesShowingCache, setCoursesShowingCache] = React.useState(false);

  // ── Providers directory (cne_providers + course counts) ──────────────
  const [providers,        setProviders]        = React.useState([]);
  const [providersLoading, setProvidersLoading] = React.useState(true);
  const [openProviderCode, setOpenProviderCode] = React.useState(null);
  const [discoverInitFilter, setDiscoverInitFilter] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;

    // Phase 1 — hydrate cache. If a previous run saved a course payload,
    // swap it into window.COURSES immediately so the UI doesn't render
    // empty (or fall back to the small data.js seed). Runs in parallel
    // with the live fetch; whichever finishes first updates the UI.
    (async () => {
      try {
        if (window.CoursesCache?.isSupported?.()) {
          const cached = await window.CoursesCache.load();
          if (!mounted) return;
          if (cached?.courses?.length) {
            window.setCourses(cached.courses);
            setCoursesVersion(v => v + 1);
            setCoursesShowingCache(true);
          }
        }
        // Same phase-1 idea for the provider label dictionary: a cold offline
        // launch should show 東華學院, not a UUID. Cheap and synchronous.
        const cachedLabels = window.DB?.cachedProviderLabels?.();
        if (mounted && cachedLabels?.length) {
          window.setProviderLabels(cachedLabels);
          setCoursesVersion(v => v + 1);
        }
      } catch (e) {
        console.warn('[Vital cache] hydrate threw', e?.message || e);
      }
    })();

    // Phase 2 — live fetch. On success we replace the cache hydration
    // with fresh data and clear the offline flags. On failure we keep
    // whatever's currently in window.COURSES (either cache or seed) and
    // raise lastCourseFetchFailed so the banner can show.
    (async () => {
      try {
        const { data, error } = await DB.fetchCourses({ status: 'active' });
        if (!mounted) return;
        if (error) {
          setLastCourseFetchFailed(true);
          setCoursesError(error.message || String(error));
          return;
        }
        if (data && data.length > 0) {
          window.setCourses(data);          // replaces window.COURSES in-place
          setCoursesVersion(v => v + 1);
          setCoursesShowingCache(false);
          setLastCourseFetchFailed(false);
        }
        // Provider chip labels (RELEASE.md §78). Deliberately AFTER the course
        // swap and never awaited before it: the catalogue must not wait on a
        // 105-row dictionary, and Discover renders correctly without it (the
        // facet falls back to a deterministic name derived from the pool).
        // Bumping coursesVersion re-renders the sheet once the names land.
        //
        // ‼️ THE FAILURE PATH SETS A STATUS, IT DOES NOT JUST RETURN. A picker
        // cannot distinguish "still loading" from "loaded and nothing matched"
        // by looking at an empty Map, and showing 「找不到相符機構」 for a
        // dictionary that merely has not arrived would push the nurse into free
        // text and lose the selection she could have made.
        DB.fetchProviderLabels().then(({ data: labels }) => {
          if (!mounted) return;
          if (!labels) { window.setProviderLabelsStatus?.('failed'); return; }
          window.setProviderLabels(labels);      // sets status 'ready' itself
          setCoursesVersion(v => v + 1);
        }).catch(() => {
          // non-fatal for Discover — the facet's fallback label still reads as a
          // name — but the picker must know it has nothing to offer.
          if (mounted) window.setProviderLabelsStatus?.('failed');
        });
      } catch (e) {
        if (mounted) {
          setLastCourseFetchFailed(true);
          setCoursesError(e.message || String(e));
        }
      } finally {
        if (mounted) setCoursesLoading(false);
      }
    })();

    // Phase 2b — archived catalogue (lifecycle_status='archived'). Loaded
    // into a SEPARATE pool (window.ARCHIVED_COURSES) so it stays out of
    // default browse and the offline cache (cache:false), but becomes
    // findable in search + the log-a-finished-course picker. Best-effort:
    // failures are non-fatal — archived search just stays empty.
    (async () => {
      try {
        const { data, error } = await DB.fetchCourses({ status: 'archived', cache: false });
        if (!mounted || error || !data) return;
        window.setArchivedCourses(data);
        setCoursesVersion(v => v + 1);
      } catch (e) {
        console.warn('[Vital] archived fetch failed', e?.message || e);
      }
    })();

    // Phase 2c — peer-signal rollups for the course cards. One call for the
    // whole catalogue (the per-course summary RPC would be 133 round trips on
    // Discover), and deliberately independent of the catalogue fetches above:
    // it must not delay them, and its failure must not touch them. A database
    // without supabase-migration-course-experience.sql 404s here, which is the
    // same "no rollups" outcome as the kill switch — cards then show no chips.
    (async () => {
      try {
        const { data, error } = await DB.fetchExperienceRollups();
        if (!mounted || error || !data) return;
        window.setExperienceRollups(data);
        setCoursesVersion(v => v + 1);
      } catch (e) {
        console.warn('[Vital] experience rollups failed', e?.message || e);
      }
    })();

    // Phase 2d — review rollups (stars + chips). Separate call from 2c on
    // purpose: they are separate server aggregates with separate kill switches,
    // and one being revoked or unmigrated must not take the other down.
    (async () => {
      try {
        const { data, error } = await DB.fetchReviewRollups();
        if (!mounted || error || !data) return;
        window.setReviewRollups(data);
        setCoursesVersion(v => v + 1);
      } catch (e) {
        console.warn('[Vital] review rollups failed', e?.message || e);
      }
    })();

    (async () => {
      try {
        const { data, error } = await DB.fetchProviders();
        if (!mounted) return;
        if (!error && data) setProviders(data);
      } catch (e) {
        console.warn('fetchProviders failed', e);
      } finally {
        if (mounted) setProvidersLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── Network status (drives the offline banner) ────────────────────────
  // We read the current status on mount and subscribe for changes. The
  // browser fallback (navigator.onLine) is wired separately so the dev
  // build behaves consistently. The banner ALSO depends on
  // lastCourseFetchFailed below — that catches the captive-portal /
  // server-down case where the OS reports connectivity but the API is
  // unreachable.
  React.useEffect(() => {
    let cancelled = false;
    let removeListener = null;

    const apply = (connected) => {
      if (!cancelled) setIsOffline(!connected);
    };

    const Net = window.CapacitorNetwork;
    if (Net) {
      (async () => {
        try {
          const status = await Net.getStatus();
          apply(status?.connected !== false);
        } catch (e) {
          console.warn('[Vital network] getStatus failed', e?.message || e);
        }
        try {
          const handle = await Net.addListener('networkStatusChange', (status) => {
            console.log('[Vital network] change:', status?.connectionType, 'connected=', status?.connected);
            apply(status?.connected !== false);
          });
          if (cancelled) handle.remove();
          else removeListener = () => handle.remove();
        } catch (e) {
          console.warn('[Vital network] addListener failed', e?.message || e);
        }
      })();
    } else if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      // Web fallback. Capacitor's Network plugin handles this on native;
      // this branch covers the browser dev path.
      apply(navigator.onLine);
      const onOnline  = () => apply(true);
      const onOffline = () => apply(false);
      window.addEventListener('online',  onOnline);
      window.addEventListener('offline', onOffline);
      removeListener = () => {
        window.removeEventListener('online',  onOnline);
        window.removeEventListener('offline', onOffline);
      };
    }

    return () => {
      cancelled = true;
      if (removeListener) removeListener();
    };
  }, []);

  // Banner visibility: OS says we're offline OR the most recent fetch
  // failed while cached data is being shown. We don't show the banner
  // merely because a fetch is in-flight on a healthy network.
  const showOfflineBanner = isOffline || (lastCourseFetchFailed && coursesShowingCache);

  React.useEffect(() => {
    let mounted = true;
    let settled = false;
    let removeListener = null;

    // ── PHASE A watchdog: no session event arrived at all ──────────────────
    //
    // Safety net — if Supabase session restoration stalls (cold start, slow
    // network, or a thrown error in detectSessionInUrl), force the app out of
    // the loading state after 4s so the user never has to manually refresh.
    //
    // ‼️ THIS COVERS ONE PHASE ONLY, AND IT USED TO COVER TWO. `settled` was not
    // set until AFTER the awaited profile query inside finalize(), so a slow
    // PROFILE fetch tripped this timer too — a completely different failure from
    // the one it is named for. It then routed 'ready' before finalize's own
    // branches ran, which can flash Home at a user who belongs in onboarding.
    // finalize() now claims the slot the moment a session is known, and the
    // profile query has its own bound (see PROFILE_LOAD_TIMEOUT_MS).
    //
    // ‼️ AND THE MESSAGE USED TO LIE. It said "entering guest mode"
    // unconditionally — including when finalize had ALREADY called setSession(s)
    // and the app was entering `ready` WITH a session. That wording sent the
    // first investigation of this warning down the wrong path entirely. It now
    // reports the phase it actually timed out in, and the two signals are
    // distinguishable in a production console.
    const fallbackTimer = setTimeout(() => {
      if (!mounted || settled) return;
      console.warn('[Vital auth] auth_bootstrap_timeout — no session event within 4s; continuing as guest.');
      settled = true;
      setAuthState('ready');
    }, 4000);

    const finalize = async (s, source) => {
      if (!mounted) return;
      try {
        // Recovery flows always take priority over normal session routing
        // so a stale or new session event can never bump the user out of
        // the recovery UI. Order below is the priority order.
        if (recoveryErrorRef.current) {
          settled = true; setAuthState('recoveryError'); return;
        }
        if (postResetRef.current) {
          settled = true; setAuthState('postReset'); return;
        }
        if (recoveryRef.current || window.location.hash.includes('type=recovery')) {
          settled = true; setAuthState('reset'); return;
        }
        if (lockedRef.current) {
          // Biometric is enrolled but the user hasn't passed the gate
          // yet. Refuse to render any signed-in UI — including the
          // case where Supabase's background token refresh fires
          // onAuthChange while we're sitting on the locked screen.
          settled = true; setAuthState('locked'); return;
        }
        if (!s) {
          setSession(null); setProfile(null);
          settled = true; setAuthState('ready'); return;
        }
        setSession(s);
        // ‼️ THE SLOT IS CLAIMED HERE, NOT AFTER THE PROFILE QUERY. A session
        // exists; phase A is over by definition, and letting its watchdog fire
        // during a slow profile load is what made it report "guest mode" at a
        // signed-in user. Everything below is phase B.
        settled = true;
        // ‼️ BOUNDED. `getProfile` is a bare fetch with no timeout of its own, so
        // before this the ONLY thing rescuing a hung profile query was phase A's
        // watchdog — which is precisely why claiming the slot above cannot be
        // done without this. Timing out resolves to `null`, which lands on the
        // SAME null-profile policy an error already used: trust the cached
        // onboarded flag, else route to onboarding. No new routing rule.
        const p = await settleWithin(
          DB.getProfile(s.user.id).catch(err => {
            console.warn('[Vital auth] profile_load_error during bootstrap', err);
            return null;
          }),
          PROFILE_LOAD_TIMEOUT_MS,
          null,
          () => console.warn('[Vital auth] profile_load_timeout — session is valid; '
            + 'routing on the cached onboarding flag.'),
        );
        if (!mounted) return;
        setProfile(p);
        // Cache the returning-user identity (email + display name) for the
        // SignInScreen quick-login card. Email is on the session; name comes
        // from the freshly-loaded profile (may be null offline — rememberIdentity
        // merges by email and keeps any prior name for the same user).
        try { AuthAPI.rememberIdentity?.({ email: s.user.email, name: p?.display_name }); } catch (_) {}
        // Routing rules:
        //   profile loaded, onboarded=true  → ready (and cache the flag
        //                                     so next offline launch
        //                                     routes correctly)
        //   profile loaded, onboarded=false → onboarding
        //   profile fetch failed (null)     → trust cached flag if set
        //                                     (offline / captive portal),
        //                                     fall through to onboarding
        //                                     only if we've never seen
        //                                     this user onboarded
        //
        // ⚠ ORDER-DEPENDENT — DO NOT REORDER THESE BRANCHES.
        // The wasOnboardingSkipped check MUST come before (p && !p.onboarded).
        // The SIWA App Store Review compliance path in db.js
        // signInWithApple() relies on this: it sets the skipped flag for
        // every fresh SIWA user precisely so that this branch fires before
        // the (profile exists, but onboarded=false) → 'onboarding' branch
        // would otherwise dump them into OnboardingFlow and force them to
        // re-enter the name Apple already provided. Same applies to the
        // mirrored chain inside the biometric-restore path above.
        if (p?.onboarded) {
          AuthAPI.markUserOnboarded(s.user.id);
          // If they ever skipped before and have now actually completed,
          // drop the skipped flag so the two sources of truth agree.
          AuthAPI.clearOnboardingSkipped?.(s.user.id);
          setAuthState('ready');
        } else if (AuthAPI.wasOnboardingSkipped?.(s.user.id)) {
          // User chose Skip on a previous session, OR signed in with Apple
          // (db.js auto-sets this flag for every SIWA bootstrap to satisfy
          // App Store Review §5.1.1(iv) — no post-SIWA name/email prompt).
          // They can return to onboarding via Profile → Complete profile.
          setAuthState('ready');
        } else if (p && !p.onboarded) {
          setAuthState('onboarding');
        } else if (AuthAPI.wasUserOnboarded(s.user.id)) {
          console.log('[Vital] profile fetch failed but cached onboarded flag is set → ready');
          setAuthState('ready');
        } else {
          setAuthState('onboarding');
        }
      } catch (e) {
        console.warn('Auth finalize failed', e);
        if (mounted) { settled = true; setAuthState('ready'); }
      }
    };

    // Parse a vital:// deep link (or a web recovery URL passed via location).
    // Sets one of the recovery refs and the corresponding authState BEFORE
    // the normal getSession() flow runs, so home is never the fallback for
    // a recovery link — valid, expired, or malformed.
    const handleDeepLink = async (url) => {
      if (!url || !mounted) return;
      console.log('[Vital deep-link] handling URL:', url);

      const looksLikeApp = typeof url === 'string'
        && url.toLowerCase().startsWith('vital://');

      // Classify BEFORE the bootstrap slot is claimed: a navigation link must
      // not divert the normal session flow (cold start still lands on home /
      // sign-in as usual; the stashed target is consumed once authState
      // reaches 'ready'). The whole contract — allowlist, auth-material veto,
      // https share hosts — lives in data.js `classifyDeepLink` so app.jsx and
      // scripts/test-deep-links.mjs can never disagree. Anything it does not
      // recognise stays 'recovery' and falls through untouched below.
      //
      // ‼️ If you revert this to an inline allowlist, `vital://wallet` lands on
      // the recovery-error screen (build 16 shipped exactly that way — data.js
      // had the classifier, app.jsx never called it). `npm run test:deep-links`
      // catches it: "every nav target is routed in app.jsx".
      const cls = (typeof window.classifyDeepLink === 'function')
        ? window.classifyDeepLink(url)
        : { kind: 'recovery' };

      if (cls.kind === 'nav') {
        console.log('[Vital deep-link] nav link -> ' + cls.target);
        pendingNavRef.current = { target: cls.target, params: cls.params };
        setNavNonce((n) => n + 1);
        return;
      }
      if (cls.kind === 'ignore') {
        // An https share host with no route for us. ALWAYS return: falling
        // through would let the recovery parser treat it as a malformed
        // recovery link and strand the user on the recovery-error screen.
        console.log('[Vital deep-link] share host, no route - ignoring');
        return;
      }

      // Claim the bootstrap slot the moment we see any (non-nav) vital://
      // URL. Even if parsing or setSession fails below, we WILL route the
      // user into a recovery-flow state — never to home.
      if (looksLikeApp) settled = true;

      try {
        const u = new URL(url);
        const raw = u.hash.startsWith('#') ? u.hash.slice(1)
                  : u.search.startsWith('?') ? u.search.slice(1)
                  : '';
        const params = new URLSearchParams(raw);
        console.log('[Vital deep-link] param keys:', Array.from(params.keys()).join(','));

        const error            = params.get('error');
        const errorCode        = params.get('error_code');
        const errorDescription = params.get('error_description');
        const access_token     = params.get('access_token');
        const refresh_token    = params.get('refresh_token');
        const type             = params.get('type');

        // Expired / invalid link — Supabase returns error params instead
        // of tokens. We never call setSession because there's nothing to
        // set; route directly to the dedicated error screen.
        if (error || errorCode || errorDescription) {
          console.log('[Vital deep-link] error params → recoveryError');
          recoveryErrorRef.current = {
            code: errorCode || error || 'unknown',
            description: errorDescription || 'The recovery link is invalid or expired.',
          };
          setAuthState('recoveryError');
          return;
        }

        if (access_token && refresh_token) {
          // type distinguishes the intent of the link:
          //   'recovery' → password reset; route to ResetPasswordScreen via
          //                recoveryRef so finalize() doesn't drop the user
          //                on home with a recovery-scope session.
          //   'signup'   → email confirmation after sign-up; just install
          //                the session and let finalize() route to
          //                onboarding (or home if already onboarded).
          //   absent     → conservatively treat as recovery — this is the
          //                pre-existing behaviour for vital:// links that
          //                arrived without a type query param.
          const isSignup = type === 'signup';

          if (!isSignup) {
            // Set recoveryRef BEFORE setSession. setSession synchronously
            // triggers onAuthStateChange → finalize(), and finalize must
            // see recoveryRef === true so it routes to 'reset' instead of
            // 'ready' / 'onboarding' when the brand-new recovery session
            // appears. This was the cold-path bug: the previous order
            // left a window where finalize ran with recoveryRef === false
            // and committed authState='ready' before we could override it.
            recoveryRef.current = true;
            setAuthState('reset');
          }

          const { error: setErr } = await window.sb.auth.setSession({ access_token, refresh_token });
          if (setErr) {
            console.warn('[Vital deep-link] setSession failed', setErr);
            if (!isSignup) recoveryRef.current = false;
            recoveryErrorRef.current = {
              code: 'session_error',
              description: setErr.message || 'Could not start a recovery session.',
            };
            setAuthState('recoveryError');
            return;
          }

          if (isSignup) {
            // setSession synchronously fired onAuthStateChange → finalize,
            // which routes to 'onboarding' (or 'ready' if already
            // onboarded) based on the loaded profile. Nothing more to do
            // here — just don't claim 'reset'.
            console.log('[Vital deep-link] setSession ok, type=signup — handed off to finalize');
          } else {
            console.log('[Vital deep-link] setSession ok, type=', type, '— locked to reset');
          }
          return;
        }

        // vital:// URL with neither tokens nor error params — almost
        // always a malformed redirect. Surface as a generic recovery
        // error so we still never fall through to home.
        console.warn('[Vital deep-link] no tokens or error params → recoveryError');
        recoveryErrorRef.current = {
          code: 'no_tokens',
          description: 'The recovery link could not be processed. Please request a new one.',
        };
        setAuthState('recoveryError');
      } catch (e) {
        console.warn('[Vital deep-link] parse failed', url, e);
        recoveryErrorRef.current = {
          code: 'parse_error',
          description: 'The recovery link could not be opened.',
        };
        setAuthState('recoveryError');
      }
    };

    // Web counterpart to handleDeepLink. On web, detectSessionInUrl=true means
    // supabase-js has already parsed the recovery/signup hash, installed the
    // session, and stripped the hash from window.location by the time this
    // effect runs — so finalize() can no longer read type=recovery from the
    // live URL and would route a recovery session straight to 'ready' (the
    // "reset link logs me in" bug). supabase-config.js snapshots the launch
    // URL BEFORE createClient runs; here we read that snapshot to set the
    // routing intent. We do NOT touch the session — supabase already installed
    // it; we only decide where the user lands.
    //
    // MUST run synchronously before the onAuthChange listener is registered
    // so recoveryRef is set before the first INITIAL_SESSION / PASSWORD_RECOVERY
    // event reaches finalize().
    const handleWebAuthLaunch = () => {
      if (!!globalThis.Capacitor?.isNativePlatform?.()) return; // native owns vital:// links
      try {
        const snap = window.__VITAL_LAUNCH_AUTH__;
        if (!snap) return;
        const hash = snap.hash || '';
        const search = snap.search || '';
        const raw = hash.startsWith('#') ? hash.slice(1)
                  : search.startsWith('?') ? search.slice(1)
                  : '';
        if (!raw) return;
        const params = new URLSearchParams(raw);
        const error            = params.get('error');
        const errorCode        = params.get('error_code');
        const errorDescription = params.get('error_description');
        const type             = params.get('type');

        // Expired / tampered recovery link — Supabase returns error params
        // instead of tokens. Route to the dedicated error screen so we never
        // fall through to home, mirroring the native error path.
        if (error || errorCode || errorDescription) {
          console.log('[Vital web-auth] error params → recoveryError');
          recoveryErrorRef.current = {
            code: errorCode || error || 'unknown',
            description: errorDescription || 'The recovery link is invalid or expired.',
          };
          settled = true;
          setAuthState('recoveryError');
          return;
        }

        // Only an explicit type=recovery routes to the reset screen. We must
        // NOT use a "tokens but no type ⇒ recovery" heuristic here (the native
        // path can, because vital:// only ever carries recovery/signup): on
        // web, the Apple OAuth callback also returns a token-bearing hash with
        // no type, and treating that as recovery would trap a normal sign-in
        // on the reset-password screen. type=signup is left for finalize() to
        // route normally (email confirmation → onboarding/ready).
        if (type === 'recovery') {
          console.log('[Vital web-auth] type=recovery → reset');
          recoveryRef.current = true;
          settled = true;
          setAuthState('reset');
        }
      } catch (e) {
        console.warn('[Vital web-auth] launch URL parse failed', e?.message || e);
      } finally {
        // One-shot: clear so a later in-session reload of this effect can't
        // re-trigger recovery from a stale snapshot.
        try { window.__VITAL_LAUNCH_AUTH__ = null; } catch (_) {}
      }
    };

    // Web share-link launch (app.vitalcne.com). A shared /c or /g link opens the
    // web app as a ROOT query param (?course= / ?join= / ?ref=) — the app host
    // has no path rewrites, and the marketing-site landing pages' "open on web"
    // button targets this. Native delivers the same via handleDeepLink instead.
    // Routes course/group like a nav link, then strips the params so a reload
    // can't re-fire.
    const handleWebShareLaunch = () => {
      if (!!globalThis.Capacitor?.isNativePlatform?.()) return; // native owns deep links
      try {
        const loc = window.location;
        const params = new URLSearchParams(loc.search || '');
        const join = params.get('join') || params.get('group');
        const course = params.get('course');
        const gm = loc.pathname.match(/^\/g\/([^/]+)/);   // path forms too, if ever rewritten
        const cm = loc.pathname.match(/^\/c\/([^/]+)/);
        let pending = null;
        if (join || gm) {
          pending = { target: 'group', params: { code: String(join || decodeURIComponent(gm[1])).toUpperCase() } };
        } else if (course || cm) {
          pending = { target: 'course', params: { id: course || decodeURIComponent(cm[1]) } };
        }
        if (pending) {
          pendingNavRef.current = pending;
          setNavNonce((n) => n + 1);
          // Drop the share params (keep any auth hash) so reload/reshare of the
          // current URL doesn't re-trigger the flow.
          try { window.history.replaceState({}, '', loc.pathname.replace(/^\/(g|c)\/[^/]+/, '/') + loc.hash); } catch (_) {}
        }
      } catch (e) { console.warn('[Vital web-share] parse failed', e?.message || e); }
    };

    let unsubFn = null;

    (async () => {
      const CapApp = window.CapacitorApp;

      // 0. Fresh-install detection — runs first because Keychain
      //    survives uninstall and would otherwise mislead the biometric
      //    enrolment check below. If localStorage has no install marker
      //    this is a fresh app container; wipe any leftover biometric
      //    Keychain entry so hasStoredCredentials() returns the truth.
      try { if (window.detectFreshInstall) await window.detectFreshInstall(); }
      catch (_) {}

      // 0.5. Offline short-circuit. When the OS reports no connectivity
      //      at cold launch, skip the entire auth flow and route straight
      //      to OfflineCatalogueScreen. The user gets browsing-only
      //      access to the cached course catalogue (or the bundled
      //      fallback if cache is empty). Sign-in, biometric unlock,
      //      profile, records — none would work offline, so we don't
      //      attempt any of them. They re-engage when the user taps
      //      "Try to reconnect" (which reloads the WebView).
      //
      //      In-session network drops (user already past this point)
      //      are NOT routed here — the existing offline banner shows
      //      instead so the user doesn't lose their current screen.
      try {
        const Net = window.CapacitorNetwork;
        if (Net) {
          const status = await Net.getStatus();
          if (status && status.connected === false) {
            console.log('[Vital] cold launch offline — routing to offlineBrowse');
            // Hydrate cache into window.COURSES if present. If not, the
            // bundled fallback from data.js stays in place. Either way,
            // the catalogue renders.
            if (window.CoursesCache?.isSupported?.()) {
              const cached = await window.CoursesCache.load().catch(() => null);
              if (cached?.courses?.length) {
                window.setCourses(cached.courses);
                setCoursesVersion(v => v + 1);
                setCoursesShowingCache(true);
              }
            }
            setIsOffline(true);
            settled = true;
            setAuthState('offlineBrowse');
            return; // skip biometric, getSession, deep link, everything
          }
        }
      } catch (e) {
        console.warn('[Vital] network status check failed; continuing with normal bootstrap', e?.message || e);
      }

      // 0.7. Web recovery-link check. Reads the launch-URL snapshot taken in
      //      supabase-config.js and sets recoveryRef / recoveryErrorRef BEFORE
      //      the onAuthChange listener is registered, so a web password-reset
      //      link routes to the reset screen instead of logging the user in.
      //      No-op on native (which routes vital:// links via handleDeepLink).
      //      Recovery takes priority over the biometric lock, matching native.
      handleWebAuthLaunch();
      // Web share-link routing (course / ward-group / referral). No-op on native.
      handleWebShareLaunch();

      // 1. Biometric check FIRST — must run before any onAuthChange
      //    listener is registered. supabase-js fires INITIAL_SESSION
      //    synchronously after onAuthStateChange() with whatever it
      //    auto-restored from localStorage; if lockedRef isn't set by
      //    that moment, finalize() commits authState='ready' with the
      //    persisted session before the lock gate gets a chance.
      const Bio = window.BiometricAPI;
      const biometricEnrolled = Bio?.isSupported?.()
        ? await Bio.hasStoredCredentials().catch(() => false)
        : false;
      console.log('[Vital] bootstrap: biometric enrolled =', biometricEnrolled);

      if (biometricEnrolled) {
        // Lock the UI BEFORE anything else. The persisted Supabase
        // session is allowed to live in memory — lockedRef gates it
        // from reaching the UI via finalize()'s priority chain. We do
        // NOT call signOut({scope:'local'}) anymore: it triggered a
        // race where INITIAL_SESSION fired with null and finalize
        // committed 'ready' before lockedRef was set. Holding the
        // session in memory also lets attemptBiometricUnlock prefer
        // it over a refreshSession round-trip with a possibly-stale
        // Keychain token.
        //
        // The auto Face ID prompt is owned by LockedScreen's mount
        // effect, not fired from here. That guarantees the prompt only
        // appears once the locked UI is on screen, and a failure has a
        // single, predictable path back to LockedScreen's error
        // surface — no fire-and-forget dropping escape results on the
        // floor.
        lockedRef.current = true;
        settled = true;
        setAuthState('locked');
      }

      // 2. Register onAuthChange now that lockedRef is correctly set.
      //    INITIAL_SESSION fires synchronously and finalize routes to
      //    'locked' via the priority chain. Subsequent SIGNED_IN /
      //    TOKEN_REFRESHED / SIGNED_OUT events keep flowing through
      //    finalize as before.
      unsubFn = AuthAPI.onAuthChange((s, event) => {
        // Belt-and-suspenders for the web recovery flow: supabase-js emits
        // PASSWORD_RECOVERY when it parses a recovery link. The launch snapshot
        // is the primary signal; this is a backup for when the event fires
        // before we subscribe. recoveryRef feeds finalize's priority chain on
        // EVERY event (incl. the periodic TOKEN_REFRESHED from autoRefreshToken),
        // so the re-arm is guarded to not outlive the flow: never flip back to
        // recovery once it has completed (postReset) or errored. This mirrors
        // finalize's own error > postReset > reset priority order. (A
        // `!recoveryRef.current` guard would be pointless here — re-affirming
        // true while already on the reset screen is a no-op, and recoveryRef is
        // already false by the time any post-completion event could arrive.)
        if (event === 'PASSWORD_RECOVERY'
            && !postResetRef.current
            && !recoveryErrorRef.current) {
          recoveryRef.current = true;
        }

        finalize(s, 'change');

        // Mark a GENUINE sign-in so the biometric enrol prompt only fires
        // after a real login — never on the INITIAL_SESSION cold-start
        // restore that runs on every app reopen. Consumed by the
        // first-login enrol effect below.
        if (event === 'SIGNED_IN' && s?.user?.id) {
          freshSignInRef.current = s.user.id;
        }

        // OAuth re-auth deletion resume (web only). On a SIGNED_IN event,
        // check for a fresh marker stamped before the redirect: if the
        // user is the same and the stamp isn't stale, run the deletion
        // immediately. Markers from older flows or other users are
        // discarded silently. INITIAL_SESSION / TOKEN_REFRESHED don't
        // trigger this — we require a real new sign-in event so a
        // cancelled OAuth round-trip (which restores the original session
        // without firing SIGNED_IN) cannot accidentally delete the
        // account on next page load.
        if (event === 'SIGNED_IN' && s?.user?.id) {
          try {
            const raw = sessionStorage.getItem(OAUTH_REAUTH_DELETE_KEY);
            if (raw) {
              sessionStorage.removeItem(OAUTH_REAUTH_DELETE_KEY);
              const marker = JSON.parse(raw);
              const fresh = marker?.stampedAt
                && (Date.now() - marker.stampedAt) < OAUTH_REAUTH_DELETE_TTL_MS;
              if (fresh && marker.userId === s.user.id) {
                // Block the UI for the duration of the resumed cascade.
                // The pre-redirect setDeleting(true) was lost when the
                // page reloaded, so we re-arm it here.
                setDeleting(true);
                (async () => {
                  console.log('[Vital delete-account] resuming after OAuth re-auth');
                  const res = await AuthAPI.deleteAccount();
                  if (res?.error) {
                    console.warn('[Vital delete-account] resume failed:', res.error.message);
                    setDeleting(false);
                    if (typeof window.showVitalToast === 'function') {
                      window.showVitalToast(t.lang === 'zh'
                        ? `刪除失敗：${res.error.message}`
                        : `Deletion failed: ${res.error.message}`);
                    }
                    return;
                  }
                  completeAccountDeletion();
                })().catch((e) => {
                  setDeleting(false);
                  console.warn('[Vital delete-account] resume threw', e);
                });
              } else if (marker.userId && marker.userId !== s.user.id) {
                console.warn('[Vital delete-account] resume aborted — signed in as different user');
                if (typeof window.showVitalToast === 'function') {
                  window.showVitalToast(t.lang === 'zh'
                    ? '已取消刪除 — 登入了不同帳戶'
                    : 'Deletion cancelled — signed in as a different account');
                }
              }
            }
          } catch (e) {
            console.warn('[Vital delete-account] marker parse failed', e?.message || e);
            try { sessionStorage.removeItem(OAUTH_REAUTH_DELETE_KEY); } catch (_) {}
          }
        }

        (async () => {
          const B = window.BiometricAPI;
          if (!B?.isSupported?.() || !s?.user?.id || !s?.refresh_token) return;
          const has = await B.hasStoredCredentials();
          if (!has) return;
          await B.storeCredentials({
            userId: s.user.id,
            email: s.user.email,
            refresh_token: s.refresh_token,
          });
          console.log('[Vital biometric] refresh token rotated and updated in Keychain');
        })().catch((e) => console.warn('[Vital biometric] rotation skip', e?.message || e));
      });

      // 3. Cold-start deep-link check. Recovery deep links take priority
      //    over the biometric lock (recoveryRef sets in handleDeepLink
      //    BEFORE setSession, so finalize routes to 'reset' / 'recovery
      //    Error' even when lockedRef is true).
      if (CapApp) {
        try {
          const launch = await CapApp.getLaunchUrl();
          if (launch?.url) await handleDeepLink(launch.url);
        } catch (e) {
          console.warn('CapacitorApp.getLaunchUrl failed', e);
        }
        try {
          const handle = await CapApp.addListener('appUrlOpen', (event) => {
            handleDeepLink(event.url);
          });
          if (!mounted) handle.remove();
          else removeListener = () => handle.remove();
        } catch (e) {
          console.warn('CapacitorApp.addListener(appUrlOpen) failed', e);
        }
      }

      // If a cold-start deep link already routed us, or we're locked,
      // skip the normal getSession + finalize flow.
      if (settled) return;

      // 4. Normal flow — no biometric, no deep link.
      try {
        const s = await AuthAPI.getSession();
        finalize(s, 'initial');
      } catch (e) {
        console.warn('getSession failed', e);
        if (mounted && !settled) { settled = true; setAuthState('ready'); }
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(fallbackTimer);
      if (unsubFn) unsubFn();
      if (removeListener) removeListener();
    };
  }, []);

  // ── Push notification listeners (one-time on app boot) ────────────────
  // installListeners() is async (it awaits addListener so handlers are
  // attached before any event can fire). It's also idempotent — the
  // requestAndRegister path calls it again defensively, but pre-warming
  // at mount means the very first user tap can resolve faster.
  React.useEffect(() => {
    (async () => {
      try { await window.PushAPI?.installListeners?.(); }
      catch (e) { console.log('[Vital push] installListeners failed at boot', e?.message || e); }
    })();
  }, []);

  // ── Per-account token provisioning on session-ready ───────────────────────
  // Re-runs on login AND account switch (keyed on user id). If the OS already
  // granted notifications and this account hasn't explicitly turned push off,
  // silently (re)register so the new account has a deliverable device token and
  // the in-app master reflects the real OS state. NEVER prompts — ensureRegistered
  // calls register(), not requestPermissions(). Fixes "OS on but app shows off"
  // after switching accounts, and the missing-token half of "no push arrives".
  React.useEffect(() => {
    if (authState !== 'ready' || !session?.user?.id) return;
    (async () => {
      try { await window.PushAPI?.ensureRegistered?.(); }
      catch (e) { console.log('[Vital push] ensureRegistered failed', e?.message || e); }
    })();
  }, [authState, session?.user?.id]);

  // ── RevenueCat identity ──────────────────────────────────────────────
  // Configure the purchases SDK with the SUPABASE USER ID as appUserID.
  //
  // ‼️ This is load-bearing for the whole subscription chain: iap-webhook maps
  // RevenueCat's `app_user_id` straight onto entitlements.user_id. If the SDK
  // were left unconfigured (or configured anonymously), purchases would land on
  // an anonymous RevenueCat id, the webhook could not resolve a nurse, and Pro
  // would never activate — while the app showed no error at all.
  //
  // Re-runs on account switch so a second nurse on the same device is never
  // credited with the first one's subscription (configure() → logIn()).
  //
  // No-ops entirely until REVENUECAT_PUBLIC_KEY is set and the native plugin is
  // present, which is the state 1.6.0 ships in.
  React.useEffect(() => {
    if (authState !== 'ready' || !session?.user?.id) return;
    const key = window.REVENUECAT_PUBLIC_KEY;
    if (!key || !window.PurchaseAPI?.isSupported?.()) return;
    (async () => {
      try { await window.PurchaseAPI.configure(key, session.user.id); }
      catch (e) { console.log('[Vital iap] configure failed', e?.message || e); }
    })();
  }, [authState, session?.user?.id]);

  // ── Class-reminder resync on app foreground ──────────────────────────
  // Class reminders moved to server-side push (notify-cron). One-time launch
  // cleanup: cancel any local class reminders that OLDER app versions scheduled
  // on this device, so the user doesn't get a stale local reminder AND the new
  // backend push for the same class. cancelByKind only clears the reserved
  // session-reminder id namespace — never other local notifications — and is
  // idempotent, so running it on every cold start is harmless.
  React.useEffect(() => {
    const Local = window.LocalAPI;
    if (!Local?.isSupported?.() || typeof Local.cancelByKind !== 'function') return;
    Local.cancelByKind(Local.SESSION_REMINDER_KIND).catch(() => {});
  }, []);

  // ── First-login biometric enrol prompt ────────────────────────────────
  // Fires when:
  //   1. authState becomes 'ready' with a real session
  //   2. The session arrived via a REAL sign-in this run (SIGNED_IN), not a
  //      cold-start session restore (INITIAL_SESSION)
  //   3. BiometricAPI is supported on this build (native + plugins
  //      registered) AND the device has biometric available
  //   4. No Keychain credential exists for this user yet
  //   5. We haven't already asked this user
  // Otherwise it's a no-op. Each guard short-circuits early so the
  // checks are cheap.
  React.useEffect(() => {
    if (authState !== 'ready') return;
    if (!session?.user?.id) return;
    // Real-sign-in gate. freshSignInRef is set ONLY by the onAuthChange
    // SIGNED_IN handler, never by the INITIAL_SESSION cold-start restore.
    // This is what stops the sheet from re-popping on every app reopen
    // (the in-memory enrolPromptCheckedRef resets on each cold start, so
    // it alone can't distinguish a reopen from a fresh login). A sign-out
    // + sign-in still emits SIGNED_IN, so the dialog correctly reappears
    // after sign-out if biometric is still off.
    if (freshSignInRef.current !== session.user.id) return;
    // Once-per-SESSION guard. Reset to null in signOut() so a future
    // sign-in (same or different account) gets re-evaluated. We do not
    // gate on the persistent localStorage `wasPromptShownFor` flag
    // anymore — the user explicitly requested that the dialog reappear
    // after sign-out if biometric still isn't enabled, and a per-session
    // ref handles "don't repeatedly nag during the same session"
    // without forever-suppressing the prompt.
    if (enrolPromptCheckedRef.current === session.user.id) return;

    const Bio = window.BiometricAPI;
    if (!Bio?.isSupported?.()) return;

    enrolPromptCheckedRef.current = session.user.id;

    let cancelled = false;
    (async () => {
      // Only suppress if the user is ALREADY enrolled — never because
      // they declined a previous session.
      const has = await Bio.hasStoredCredentials();
      if (has) return;
      const avail = await Bio.checkAvailability();
      if (cancelled || !avail.isAvailable) return;
      setEnrolBiometryType(avail.biometryType || 'faceId');
      // Small delay so home renders first; avoids the sheet popping
      // before the user even sees what app they just signed into.
      setTimeout(() => {
        if (!cancelled) setEnrolPromptOpen(true);
      }, 900);
    })();

    return () => { cancelled = true; };
  }, [authState, session?.user?.id]);

  // ── Pull prefs from profile once, then sync changes back ─────────────
  // prefsHydrated gates the write-back below. On a fresh device / new web
  // login the local tweak state still holds the "en" default, and without
  // the gate the push effect fires the moment authState turns 'ready' —
  // racing the pull and clobbering the cloud lang back to English
  // (DB.setPrefs is an async read-merge-write, so the stale write can land
  // last). Deliberately React state, NOT a ref: the push effect's closure
  // must still see `false` on the very commit where this pull applies the
  // cloud values — a ref would already read `true` there and push stale
  // local prefs once before the pulled values re-render.
  const [prefsHydrated, setPrefsHydrated] = React.useState(false);
  React.useEffect(() => {
    if (!profile || profile.id !== session?.user?.id) {
      // Sign-out, account deletion, a profile fetch failure (offline), or the
      // brief account-switch render where the old profile still exists while
      // the new session is loading. Re-gate in all four cases: otherwise the
      // new account's widget could inherit the previous account's language.
      // Re-gate so the next sign-in — possibly a different account —
      // hydrates from the cloud before any push. Also means a session
      // whose profile never loaded pushes nothing (we can't safely merge
      // without knowing the cloud state).
      setPrefsHydrated(false);
      return;
    }
    if (profile.prefs) {
      if (profile.prefs.lang    && profile.prefs.lang    !== t.lang)    setTweak('lang',    profile.prefs.lang);
      if (profile.prefs.theme   && profile.prefs.theme   !== t.theme)   setTweak('theme',   profile.prefs.theme);
      if (profile.prefs.density && profile.prefs.density !== t.density) setTweak('density', profile.prefs.density);
    }
    // Profile loaded (prefs may be empty for a brand-new user, which is
    // fine — pushing local values is then the desired first write).
    setPrefsHydrated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, session?.user?.id]);

  React.useEffect(() => {
    if (!session || authState !== 'ready') return;
    if (!prefsHydrated) return; // never write local defaults over cloud prefs before the pull lands
    DB.setPrefs(session.user.id, { lang: t.lang, theme: t.theme, density: t.density });
  }, [t.lang, t.theme, t.density, session?.user?.id, authState, prefsHydrated]);

  // ── Apply theme + density to root ────────────────────────────────────
  React.useEffect(() => {
    const sysDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const eff = t.theme === 'auto' ? (sysDark ? 'dark' : '') : (t.theme === 'dark' ? 'dark' : '');
    document.documentElement.dataset.theme = eff;
    document.documentElement.dataset.density = t.density;
  }, [t.theme, t.density]);

  // ── Mirror current lang onto window for files that emit toasts but
  //    don't have a React lang prop in scope (e.g. record-extras hooks).
  React.useEffect(() => { window.__VITAL_LANG__ = t.lang; }, [t.lang]);

  // ── User data (only loaded when signed in) ────────────────────────────
  const [savedIds, setSavedIds]       = React.useState(new Set());
  const [enrolledIds, setEnrolledIds] = React.useState(new Set());
  const [records, setRecords]         = React.useState([]);
  const [qualifications, setQualifications] = React.useState([]);
  // ‼️ "NOT KNOWN YET" IS NOT "CONFIRMED EMPTY", and an array cannot say which
  // one it is. `qualifications` starts as [] because every consumer filters it,
  // so the value that means "we have not asked yet" is byte-identical to the one
  // that means "she owns nothing" — and the UI then states a number it has not
  // earned. That is the same shape as the wiring bug this whole line started
  // from: a plausible-looking 0 that was really an absence of data.
  //
  // The load state is therefore explicit and separate. Nothing derives network
  // state from array shape.
  const [qualificationsReady, setQualificationsReady] = React.useState(false);
  // The occupied current licences, from the identity registry (§72 C2/C4). Read
  // rather than derived: profileLicenses() would synthesize one for every
  // account, and 302 of them hold no licence at all.
  const [licenceIdentities, setLicenceIdentities] = React.useState([]);
  // ‼️ EVIDENCE GETS ITS OWN UNKNOWN, for the same reason qualifications did.
  // An empty array cannot say whether she has filed nothing or we have not
  // asked, and a card that guesses shows 「上載執業證書」 to a nurse who already
  // uploaded — then corrects itself half a second later. That is the exact claim
  // this codebase spent a week learning not to make.
  const [licenceEvidence, setLicenceEvidence] = React.useState([]);
  const [licenceEvidenceReady, setLicenceEvidenceReady] = React.useState(false);
  const [reminders, setReminders]     = React.useState([]);
  // The user id whose records have actually reached this component. A boolean
  // is unsafe across account switches: it would permit Account A's records to
  // be published briefly for Account B while its own load is still in flight.
  const widgetRecordsRef = React.useRef(null);

  // Client-side derived reminders (only meaningful for signed-in users)
  const allReminders = React.useMemo(() => {
    if (!session) return [];
    const derived = (typeof deriveReminders === 'function')
      ? deriveReminders({
          profile,
          enrolledIds: [...enrolledIds],
          records,
          courses: (typeof COURSES !== 'undefined' ? COURSES : []),
        })
      : [];
    const stored = reminders || [];
    const seen = new Set(stored.map(r => r.id));
    return [...stored, ...derived.filter(r => !seen.has(r.id))];
  }, [session, profile, enrolledIds, records, reminders]);

  // Count of in-progress courses — mirrors the 進行中 viewbar badge inside
  // RecordScreen (catalog enrolments + manual records whose end date hasn't
  // passed and that the user hasn't explicitly marked complete). Surfaced
  // on the bottom TabBar so the user sees how much active work is sitting
  // in 我的紀錄 from anywhere in the app.
  // `coursesVersion` is in the deps because the enrolment half of this count is
  // resolved against the catalogue pools, which hydrate asynchronously.
  const recordTabBadge = React.useMemo(() => {
    if (!session) return 0;
    const todayIso = (typeof window.todayLocalIso === 'function')
      ? window.todayLocalIso()
      : new Date().toISOString().slice(0, 10);
    const isDone = (r) => typeof window.isRecordCompleted === 'function'
      && window.isRecordCompleted(r);
    const inProgress = (records || [])
      .filter(x => x.date && x.date > todayIso && !isDone(x))
      .length;
    // enrolledCourseCount, NOT enrolledIds.size — only enrolments that still
    // resolve to a course have a card waiting in 我的紀錄. Counting the rest put
    // a permanent unclearable badge on the tab bar. See data.js →
    // resolveEnrolledCourses.
    return window.enrolledCourseCount(enrolledIds) + inProgress;
  }, [session, records, enrolledIds, coursesVersion]);

  const reloadUserData = React.useCallback(async () => {
    if (!session) return;
    // Stash the session on window so record-extras.jsx (which doesn't
    // receive session via props) can read it inside its hook setters
    // to attach userId to attendance upserts.
    window._vital_session_ = session;
    const [s, e, r, q, li, lev, rm, att] = await Promise.all([
      DB.getSaved(session.user.id),
      DB.getEnrolled(session.user.id),
      DB.getRecords(session.user.id),
      DB.getQualifications ? DB.getQualifications(session.user.id) : Promise.resolve([]),
      DB.getLicenceIdentities ? DB.getLicenceIdentities(session.user.id) : Promise.resolve([]),
      DB.getLicenceEvidence ? DB.getLicenceEvidence(session.user.id) : Promise.resolve({ data: [] }),
      DB.getReminders(session.user.id),
      DB.getCourseAttendance ? DB.getCourseAttendance(session.user.id) : Promise.resolve(null),
    ]);
    setSavedIds(new Set(s));
    setEnrolledIds(new Set(e));
    // Mark this session only as its records become available. Widget publishing
    // must never interpret the initial [] render as an honest zero balance.
    widgetRecordsRef.current = session.user.id;
    setRecords(r);
    setQualifications(q || []);
    setLicenceIdentities(li || []);
    // Ready even when the read errored: "we asked and got nothing back" is still
    // not "unknown forever", and leaving the card in permanent limbo would hide
    // the upload action from everyone.
    setLicenceEvidence((lev && lev.data) || []);
    setLicenceEvidenceReady(true);
    // Ready only AFTER the value lands, so a render between the two cannot claim a
    // count. Guests never reach here, which is correct: an unauthenticated
    // wallet is unknown, not empty.
    setQualificationsReady(true);
    setReminders(rm);
    // Hydrate the attendance cache before record-extras hooks read it.
    // Safe to call even on guests — hydrate() will write empty maps and
    // signal the hooks to refresh.
    if (att && typeof window.hydrateAttendance === 'function') {
      window.hydrateAttendance(att);
    }
  }, [session]);

  React.useEffect(() => {
    if (authState === 'ready' && session) reloadUserData();
  }, [authState, session, reloadUserData]);

  // Publish exactly one privacy-safe WidgetKit snapshot. The two signed-in
  // gates deliberately prefer an older truthful snapshot over a newly-built
  // but incomplete one during offline boot, a record-load race, or account
  // switch. WidgetAPI byte-dedupes identical payloads before touching iOS.
  React.useEffect(() => {
    if (authState !== 'ready') return;

    if (!session) {
      const signedOut = window.buildWidgetSnapshot?.({
        profile: null, records: [], lang, today: window.todayIso?.(),
      });
      if (signedOut) window.WidgetAPI?.publish?.(signedOut);
      return;
    }

    // A null profile can be a transient/offline fetch failure, not a logout.
    if (!prefsHydrated) return;
    // Records are the source of `earned`; do not publish until this session's
    // load has completed (and never accept a previous account's load).
    if (widgetRecordsRef.current !== session.user.id) return;

    const snapshot = window.buildWidgetSnapshot?.({
      profile, records, lang, today: window.todayIso?.(),
    });
    if (snapshot) window.WidgetAPI?.publish?.(snapshot);
  }, [authState, session?.user?.id, profile, records, lang, prefsHydrated]);

  // ── Auth overlay + requireAuth helper ─────────────────────────────────
  // authOverlay: null | { intent: 'save'|'enroll'|'log'|'record'|'saved'|'reminders'|'default' }
  const [authOverlay, setAuthOverlay] = React.useState(null);
  const pendingActionRef = React.useRef(null);

  // When the user successfully signs in, run any queued action and close.
  React.useEffect(() => {
    if (session && pendingActionRef.current) {
      const fn = pendingActionRef.current;
      pendingActionRef.current = null;
      setAuthOverlay(null);
      // Defer so reloadUserData() has a chance to run first.
      setTimeout(() => fn(session), 0);
    }
    if (session && authOverlay) {
      // Signed in with no pending action — just close.
      setAuthOverlay(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const requireAuth = React.useCallback((action, intent) => {
    if (session) { action?.(session); return; }
    pendingActionRef.current = action || null;
    setAuthOverlay({ intent: intent || 'default' });
  }, [session]);

  const closeAuthOverlay = React.useCallback(() => {
    pendingActionRef.current = null;
    setAuthOverlay(null);
  }, []);

  // ── Gated actions (auto-prompt sign-in when needed) ───────────────────
  // Offer the push soft-ask once, after a value moment. Gated so it never
  // burns the one-shot iOS dialog: shown only when push is supported, the user
  // hasn't seen it before, and they haven't already granted OR denied at the
  // OS level (a denied dialog won't re-appear, so priming would dead-end).
  // We mark it shown the moment it opens so "Not now" never nags again.
  const maybeOfferPush = React.useCallback(async (moment) => {
    try {
      if (pushSoftAskFiredRef.current) return;
      if (!window.PushAPI?.isSupported?.()) return;
      const uid = session?.user?.id;
      if (!uid || !window.DB?.wasPushSoftAskShown) return;
      if (window.DB.wasPushSoftAskShown(uid)) return;
      const perm = await window.PushAPI.checkPermissions();
      const recv = perm?.receive;
      if (recv === 'granted' || recv === 'denied') return; // already decided at OS level
      pushSoftAskFiredRef.current = true;
      window.DB.markPushSoftAskShown(uid);
      setPushSoftAsk({ moment });
    } catch (_) { /* never block the value-moment action on the soft-ask */ }
  }, [session?.user?.id]);

  // Offer the review-TRIAGE sheet after a genuine satisfaction moment —
  // completing (logging) a CNE record. The sheet (ReviewAskSheet) routes
  // 好用 → Vital's App Store "Write a Review" page, 有意見 → FeedbackSheet.
  // The positive route is an explicit user-initiated deep link rather than
  // StoreKit requestReview(): Apple may silently suppress that API, so it can
  // never fulfil a button's promise to take the user somewhere. Best-practice
  // gating keeps this contextual and infrequent:
  //   • native iOS only (the rating sheet has no web equivalent);
  //   • PROVEN, returning user — ≥3 records before this log (a habit, not a
  //     first-run reflex) AND an account ≥7 days old (value experienced over
  //     time; blocks the day-1 history-backfill session);
  //   • never in the same session as the push soft-ask, and only after that
  //     ask has had its turn — one ask per session, ever;
  //   • our own 120-day cooldown on top of Apple's 3×/365d cap (a prompt the
  //     OS chose not to show mustn't burn every future value moment), marked
  //     when the sheet OPENS — a dismissal is a spent ask, 稍後 never nags;
  //   • fired 1.2s after the save so it lands on the updated progress view,
  //     not over the closing Log sheet/toast; if the app went to background
  //     during that settle, we skip WITHOUT spending the cooldown;
  //   • once per session, fire-and-forget — never blocks the record save.
  const maybeOfferReview = React.useCallback(async () => {
    try {
      if (reviewFiredRef.current) return;
      if (!window.ReviewAPI?.isSupported?.()) return;
      const uid = session?.user?.id;
      if (!uid || !window.DB?.lastReviewPromptedAt) return;
      if (records.length < 3) return;                          // engagement gate
      const MIN_ACCOUNT_AGE_MS = 7 * 24 * 60 * 60 * 1000;      // 7 days
      const createdAt = Date.parse(session?.user?.created_at || '');
      if (!createdAt || Date.now() - createdAt < MIN_ACCOUNT_AGE_MS) return;
      if (pushSoftAskFiredRef.current) return;                 // never stack two asks in one session
      // Push priming keeps strict priority — but only while it still has a
      // PENDING claim on a future value moment: not yet shown AND the OS
      // permission still undecided (the same eligibility maybeOfferPush uses).
      // A user who enabled/denied push from Settings long ago never needs the
      // soft-ask, and must not be locked out of the review ask forever.
      if (!window.DB.wasPushSoftAskShown(uid) && window.PushAPI?.isSupported?.()) {
        const perm = await window.PushAPI.checkPermissions().catch(() => null);
        const recv = perm?.receive;
        if (recv !== 'granted' && recv !== 'denied') return;   // soft-ask goes first
      }
      const last = window.DB.lastReviewPromptedAt(uid);
      const COOLDOWN_MS = 120 * 24 * 60 * 60 * 1000;           // 120 days
      if (last && Date.now() - last < COOLDOWN_MS) return;
      reviewFiredRef.current = true;
      const SETTLE_MS = 1200;                                  // Log sheet close + toast fade
      setTimeout(() => {
        try {
          if (document.hidden) return; // backgrounded — don't spend the ask unseen
          window.DB.markReviewPrompted(uid, Date.now());
          setReviewAsk({});
        } catch (_) { /* best-effort */ }
      }, SETTLE_MS);
    } catch (_) { /* never block the value moment on the review ask */ }
  }, [session?.user?.id, session?.user?.created_at, records.length]);

  // D2 — one-time "pick a community handle" invite. Best-practice contextual
  // consent: we never ask at onboarding; we ask only once a contribution has
  // actually been approved (so the value is concrete). inviteCheckedRef keeps
  // it to a single probe per session; community_handle_invited_at is stamped
  // the moment the sheet opens so it can never nag again, even if the user
  // dismisses without picking a handle.
  const maybeOfferHandleInvite = React.useCallback(async () => {
    try {
      if (inviteCheckedRef.current) return;
      const uid = session?.user?.id;
      if (!uid || !profile) return;
      if (profile.community_handle || profile.community_handle_invited_at) return;
      const has = await window.DB?.hasApprovedContribution?.(uid);
      if (!has) return;
      inviteCheckedRef.current = true;
      const stamp = new Date().toISOString();
      await window.DB.upsertProfile(uid, { community_handle_invited_at: stamp });
      setProfile(p => (p ? { ...p, community_handle_invited_at: stamp } : p));
      setHandleInvite({});
    } catch (_) { /* invite is best-effort; never block the app */ }
  }, [session?.user?.id, profile]);

  // Fires on profile load / post-sign-in (covers cold start + fresh approval
  // seen on next launch). A ref feeds the resume listener below so a
  // background approval is caught when the user returns without a full reload.
  React.useEffect(() => { maybeOfferHandleInvite(); }, [maybeOfferHandleInvite]);
  const inviteFnRef = React.useRef(() => {});
  inviteFnRef.current = maybeOfferHandleInvite;
  React.useEffect(() => {
    let sub;
    (async () => {
      try {
        const CapApp = window.CapacitorApp;
        if (!CapApp?.addListener) return;
        sub = await CapApp.addListener('appStateChange', (s) => { if (s?.isActive) inviteFnRef.current?.(); });
      } catch (_) { /* resume listener is optional */ }
    })();
    return () => { try { sub?.remove?.(); } catch (_) {} };
  }, []);

  // One-time "what's new: multiple certificates" announcement. Shown once to an
  // EXISTING onboarded user (localStorage flag unset) the first time they open
  // the updated app — brand-new users never see it because onboarding already
  // sets the flag (see the OnboardingFlow onDone/onSkip below), and the current
  // onboarding exposes the capability directly. Best-effort + never blocks.
  const WHATSNEW_DUAL_KEY = 'vital_whatsnew_dual_license';
  const dismissWhatsNewDual = React.useCallback(() => {
    try { localStorage.setItem(WHATSNEW_DUAL_KEY, '1'); } catch (_) {}
    setWhatsNewDual(null);
  }, []);
  React.useEffect(() => {
    try {
      if (whatsNewCheckedRef.current) return;
      if (authState !== 'ready' || !session?.user?.id || !profile?.onboarded) return;
      if (localStorage.getItem(WHATSNEW_DUAL_KEY) === '1') return;
      whatsNewCheckedRef.current = true;
      setWhatsNewDual({});
    } catch (_) { /* announcement is best-effort */ }
  }, [authState, session?.user?.id, profile?.onboarded]);

  const toggleSaved = React.useCallback((id) => {
    requireAuth(async (sess) => {
      const have = savedIds.has(id);
      setSavedIds(prev => { const n = new Set(prev); have ? n.delete(id) : n.add(id); return n; });
      if (typeof window.showVitalToast === 'function') {
        window.showVitalToast(have
          ? (t.lang === 'zh' ? '已取消收藏' : 'Removed from saved')
          : (t.lang === 'zh' ? '已收藏' : 'Saved'));
      }
      await DB.toggleSaved(sess.user.id, id, have);
      // Value moment — only on SAVE (not unsave).
      if (!have) maybeOfferPush('save');
    }, 'save');
  }, [savedIds, requireAuth, t.lang, maybeOfferPush]);

  const toggleEnrolled = React.useCallback((id) => {
    requireAuth(async (sess) => {
      const have = enrolledIds.has(id);
      setEnrolledIds(prev => { const n = new Set(prev); have ? n.delete(id) : n.add(id); return n; });
      await DB.toggleEnrolled(sess.user.id, id, have);
    }, 'enroll');
  }, [enrolledIds, requireAuth]);

  // Reactions go through the sign-in gate like save/enroll. Resolves the fresh
  // server summary (incl. is_upgraded) so CourseDetailScreen reconciles its
  // optimistic count; resolves { deferred: true } when we had to open the
  // sign-in overlay, so the screen reverts its optimistic bump and the user
  // re-taps after signing in.
  const toggleCourseReaction = React.useCallback((courseId, type) => new Promise((resolve) => {
    if (!session) { requireAuth(null, 'react'); resolve({ deferred: true }); return; }
    requireAuth(async () => {
      // Resolve the SUMMARY object itself (helpful_count / my_reactions /
      // is_upgraded) — NOT the { data, error } wrapper — because CourseDetail's
      // onReact consumes the resolved value directly as the new reactionSummary.
      const { data, error } = await DB.toggleCourseReaction(courseId, type);
      resolve(error ? { error } : (data || {}));
    }, 'react');
  }), [session, requireAuth]);

  const addRecord = React.useCallback((rec) => new Promise((resolve) => {
    requireAuth(async (sess) => {
      const { data, error } = await DB.addRecord(sess.user.id, { ...rec, courseId: rec.courseId || null });
      if (error) { console.warn('addRecord failed', error); resolve({ error }); return; }
      let certificatePath = null;
      if (rec.certificateFile && data?.id) {
        try {
          const up = await DB.uploadCertificate(sess.user.id, data.id, rec.certificateFile);
          certificatePath = up?.path || null;
          if (up?.error) console.warn('uploadCertificate failed', up.error);
        } catch (e) { console.warn('uploadCertificate threw', e); }
      }
      // AI/manual dual-save: one physical certificate, linked from the
      // qualification version to the CNE record that earned it.
      if (rec.qualification && data?.id && DB.saveQualificationVersion) {
        const qRes = await DB.saveQualificationVersion({
          ...rec.qualification,
          sourceRecordId: data.id,
          evidencePath: certificatePath,
          requestId: rec.qualification.requestId || globalThis.crypto?.randomUUID?.() || null,
        });
        if (qRes?.error) {
          await reloadUserData();
          resolve({ data, qualificationError: qRes.error });
          return;
        }
      }
      await reloadUserData();
      resolve({ data });
      // Value moment — successful record log. The push soft-ask fires on the
      // FIRST log; the review-triage sheet is gated to later logs (≥3 records,
      // ≥7-day-old account, never the soft-ask's session) so the two never
      // collide. Both are fire-and-forget.
      maybeOfferPush('record');
      maybeOfferReview();
    }, 'log');
  }), [reloadUserData, requireAuth, maybeOfferPush, maybeOfferReview]);

  const saveQualification = React.useCallback(async (payload, file) => {
    if (!session) return { error: { message: 'Not signed in' } };
    const res = await DB.saveQualificationVersion({
      ...payload,
      requestId: payload.requestId || globalThis.crypto?.randomUUID?.() || null,
    });
    if (res?.error) return res;
    const q = res.data?.qualification;
    const v = res.data?.version;
    if (file && q?.id && v?.id) {
      const up = await DB.uploadQualificationEvidence(session.user.id, q.id, v.id, file);
      if (up?.error) {
        await reloadUserData();
        return { data: res.data, evidenceError: up.error };
      }
    }
    await reloadUserData();
    return res;
  }, [session, reloadUserData]);

  const updateQualification = React.useCallback(async (versionId, payload) => {
    const res = await DB.updateQualificationVersion(versionId, payload);
    await reloadUserData();
    return res;
  }, [reloadUserData]);

  const archiveQualification = React.useCallback(async (id, archived) => {
    const res = await DB.setQualificationArchived(id, archived);
    await reloadUserData();
    return res;
  }, [reloadUserData]);

  const removeQualificationVersion = React.useCallback(async (id) => {
    const res = await DB.deleteQualificationVersion(id);
    await reloadUserData();
    return res;
  }, [reloadUserData]);

  // Sign-out returns the wallet to UNKNOWN. Leaving it "ready with 0 rows" would
  // let the next screen assert that the newly signed-in nurse owns nothing,
  // before anything has been fetched for her.
  React.useEffect(() => { if (!session) { setQualificationsReady(false); setLicenceIdentities([]); setLicenceEvidence([]); setLicenceEvidenceReady(false); } }, [session]);

  // ‼️ ONE UNIVERSE, BUILT ONCE. Every surface that claims a wallet number reads
  // this exact array — the personal-page count, the wallet rows, the 全部 chip,
  // nearest expiry. Handing each screen the raw inputs and letting it derive its
  // own is how Personal said 1 while the wallet said 0.
  const walletUniverse = React.useMemo(() => window.walletItems({
    licences: window.licencesForWallet(profile, licenceIdentities),
    qualifications,
  }), [profile, licenceIdentities, qualifications]);

  const signOut = React.useCallback(async () => {
    // Wipe Keychain biometric credentials BEFORE Supabase signOut so an
    // onAuthChange listener with stale refs can't race us and re-write
    // them. Per the Step 8 spec, sign-out always disables biometric —
    // re-enabling requires the user to opt in again from Profile.
    if (window.BiometricAPI?.isSupported?.()) {
      try { await window.BiometricAPI.clearCredentials(); } catch (_) {}
    }
    // Also remove this device's APNs tokens BEFORE signOut so the user's
    // JWT still passes RLS on the delete. After signOut, RLS would block
    // them from touching their own row. iOS retains the OS-level
    // notification permission — re-enabling reminders later just needs
    // PushAPI.requestAndRegister().
    if (window.PushAPI?.isSupported?.()) {
      try { await window.PushAPI.clearTokensForCurrentUser(); } catch (_) {}
    }
    await AuthAPI.signOut();
    setSavedIds(new Set()); setEnrolledIds(new Set()); setRecords([]); setReminders([]);
    setProfile(null); setSession(null);
    // Clear the attendance localStorage cache so the next account on this
    // device doesn't see the previous user's ticks before hydrate() runs.
    if (typeof window.clearAttendanceCache === 'function') window.clearAttendanceCache();
    window._vital_session_ = null;
    setTab('home');
    // Reset the per-session enrol-prompt guard so the next sign-in
    // (same or different account) re-runs the "Enable Face ID?" check.
    // freshSignInRef is left for the next real SIGNED_IN to set — clearing
    // it here just avoids a stale userId lingering after sign-out.
    enrolPromptCheckedRef.current = null;
    freshSignInRef.current = null;
    setAuthState('ready');     // stay in guest mode after sign-out
  }, []);

  // Shared teardown — runs AFTER the server-side cascade returns ok. Wipes
  // in-memory state and routes to signed-out home, identical to signOut().
  // Lifted out of deleteAccount so the web Apple re-auth resume hook can
  // call it from outside React's normal event flow.
  const completeAccountDeletion = React.useCallback(() => {
    recoveryRef.current = false;
    recoveryErrorRef.current = null;
    postResetRef.current = false;
    setSavedIds(new Set()); setEnrolledIds(new Set()); setRecords([]); setReminders([]);
    setProfile(null); setSession(null);
    setSheet(null);
    setTab('home');
    setAuthState('ready');
    setDeleting(false);

    if (typeof window.showVitalToast === 'function') {
      window.showVitalToast(t.lang === 'zh'
        ? '帳戶與資料已刪除'
        : 'Your account and data have been deleted');
    }
  }, [t.lang]);

  // sessionStorage key for the "delete after OAuth round-trip" flow.
  // Stamped with userId + timestamp before the redirect; consumed by the
  // SIGNED_IN handler in the bootstrap effect below. Web-only — native
  // OAuth re-auth never leaves the WebView so no marker is needed there.
  const OAUTH_REAUTH_DELETE_KEY = 'vital:pending-delete-after-oauth';
  const OAUTH_REAUTH_DELETE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  // Permanent account deletion. Called by DeleteAccountSheet with whichever
  // re-auth secret applies to the user's provider:
  //   - email provider: password string → signInWithPassword re-verify
  //   - apple/google provider: null → SIWA / Google Sign-In re-auth
  //       · native: AuthAPI.signInWith*() inline, verify same user, delete
  //       · web:    stamp marker → signInWith*() (full-page redirect);
  //                 deletion resumes via the SIGNED_IN handler on return
  // Returns { ok: true } | { error: { message } } so the dialog can
  // surface failures inline. On web OAuth the promise never resolves
  // locally because the page redirects away mid-flight.
  const deleteAccount = React.useCallback(async (secret) => {
    if (!session?.user?.id) return { error: { message: 'Not signed in' } };
    const userId = session.user.id;
    const email = session.user.email;
    const provider = AuthAPI.getSessionProvider(session);
    const isOAuthProvider = provider === 'apple' || provider === 'google';

    // Validate inputs BEFORE setDeleting so we don't flash the splash
    // for an obviously broken call.
    if (!isOAuthProvider) {
      if (!email) return { error: { message: 'No email on session' } };
      if (!secret) return { error: { message: 'Password required' } };
    }

    // Block the UI the instant the user confirms — covers the re-auth
    // network call, the OAuth sheet handoff, and the eventual server
    // cascade. Cleared by completeAccountDeletion() on success or by
    // any error branch below.
    setDeleting(true);

    if (isOAuthProvider) {
      const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
      const isApple = provider === 'apple';

      if (!isNative) {
        // Web: stamp intent, redirect to OAuth provider. Deletion resumes
        // when the user returns signed in (handled by the bootstrap effect).
        try {
          sessionStorage.setItem(OAUTH_REAUTH_DELETE_KEY, JSON.stringify({
            userId, stampedAt: Date.now(),
          }));
        } catch (_) {
          setDeleting(false);
          return { error: { message: 'Could not start re-verification (storage blocked)' } };
        }
        const { error } = await (isApple ? AuthAPI.signInWithApple() : AuthAPI.signInWithGoogle());
        if (error) {
          try { sessionStorage.removeItem(OAUTH_REAUTH_DELETE_KEY); } catch (_) {}
          setDeleting(false);
          return { error: { message: error.message || `${isApple ? 'Apple' : 'Google'} sign-in failed` } };
        }
        // Redirect is in flight; promise won't resolve before navigation.
        // Leave setDeleting(true) — the splash covers the brief moment
        // before the browser navigates away.
        return { ok: true };
      }

      // Native: inline OAuth re-auth, then delete in the same tick.
      const { data, error } = await (isApple ? AuthAPI.signInWithApple() : AuthAPI.signInWithGoogle());
      if (error) {
        setDeleting(false);
        return { error: { message: error.message || `${isApple ? 'Apple' : 'Google'} sign-in failed` } };
      }
      if (data?.user?.id !== userId) {
        setDeleting(false);
        return { error: { message: `Signed in as a different ${isApple ? 'Apple' : 'Google'} account — deletion aborted` } };
      }
    } else {
      // Email/password provider: re-auth with password. Wrong-password
      // failures must NOT delete anything.
      const { error: reAuthErr } = await window.sb.auth.signInWithPassword({ email, password: secret });
      if (reAuthErr) {
        setDeleting(false);
        return { error: { message: reAuthErr.message || 'Incorrect password' } };
      }
    }

    // Server-side cascade + post-server local teardown (in db.js).
    const res = await AuthAPI.deleteAccount();
    if (res?.error) {
      setDeleting(false);
      return res;
    }

    completeAccountDeletion();
    return { ok: true };
  }, [session, completeAccountDeletion]);

  // ── Navigation state ──────────────────────────────────────────────────
  const [tab, setTab] = React.useState('home');
  const [openCourseId, setOpenCourseId] = React.useState(null);
  // Ward-group invite code stashed from a vital://group / /g/<code> deep link,
  // consumed by WardGroupScreen to pre-open its join sheet.
  const [wardGroupJoinCode, setWardGroupJoinCode] = React.useState(null);
  const [sheet, setSheet] = React.useState(null);
  // A completed, server-derived snapshot lets a Pro entry point render the
  // correct first frame while ProSheet performs its own fresh read. It is
  // presentation-only and is cleared on close/account change; privileged
  // operations never trust it.
  const [proSheetInitialEnt, setProSheetInitialEnt] = React.useState(null);

  React.useEffect(() => {
    setProSheetInitialEnt(null);
  }, [session?.user?.id]);

  // Run after the biometric prompt's 900ms scheduling window. If biometric (or
  // any other priority sheet) appears, this effect is cleaned up and retried
  // only after it closes; two modal sheets therefore never stack.
  React.useEffect(() => {
    if (!pendingFirstScanRef.current || authState !== 'ready' || !session) return;
    if (enrolPromptOpen || pushSoftAsk || handleInvite || whatsNewDual || reviewAsk || sheet) return;
    const timer = setTimeout(() => {
      if (!pendingFirstScanRef.current) return;
      if (enrolPromptOpen || pushSoftAsk || handleInvite || whatsNewDual || reviewAsk) return;
      pendingFirstScanRef.current = false;
      setSheet('log:ai');
    }, 1050);
    return () => clearTimeout(timer);
  }, [authState, session, enrolPromptOpen, pushSoftAsk, handleInvite, whatsNewDual, reviewAsk, sheet]);
  // Course id to log from the detail screen — an ENDED course a nurse
  // attended but hasn't recorded yet. Opens the LogSheet prefilled from
  // that catalog course (auth-gated, like every other record write).
  const [logCourseId, setLogCourseId] = React.useState(null);
  // Bumped by the AI-scan earn menu (LogSheet) to open the Submit-course
  // sheet inside Discover. A nonce (not a boolean) so repeat taps re-fire.
  const [discoverSubmitSignal, setDiscoverSubmitSignal] = React.useState(0);
  const [discoverSavedSignal, setDiscoverSavedSignal] = React.useState(0);
  // Which focused sub-section of Profile to open.
  const [profileSection, setProfileSection] = React.useState('overview');
  // Optional field target supplied by Audit readiness. Kept outside
  // ProfileScreen so a tab change can carry the exact unresolved item across.
  const [profileEditTarget, setProfileEditTarget] = React.useState(null);
  // Bumped when a Pro sheet asks to be taken to the audit pack — it lives
  // inside ProfileScreen's NCHK export sheet, which has no section of its own.
  // A nonce, so a repeat request re-opens it (same contract as
  // discoverSubmitSignal: ProfileScreen consumes it once and calls back).
  const [profileNchkSignal, setProfileNchkSignal] = React.useState(0);
  // One-shot destination carried from the Pro readiness checklist. It is a UX
  // hint only: destinations still enforce the server entitlement themselves.
  // A nonce lets the same row be tapped again after the first highlight ends.
  const [proFeatureTarget, setProFeatureTarget] = React.useState(null);
  // Account-scoped, session-only signal for the one-time activation page. The
  // event that sets it grants nothing: the page independently re-reads the
  // server entitlement before it displays congratulations or any ready state.
  const [proSuccessUserId, setProSuccessUserId] = React.useState(null);
  // Notification history is a true global overlay. It opens above whichever
  // tab/course the user is on and closing it reveals that exact context.
  const [notificationCenterOpen, setNotificationCenterOpen] = React.useState(false);
  // Set when a certificate reminder is tapped — RecordScreen reads it to
  // open that record's detail sheet, then clears it via onFocusRecordHandled.
  const [focusRecordId, setFocusRecordId] = React.useState(null);
  const [focusQualificationId, setFocusQualificationId] = React.useState(null);
  // Forces the Record tab to open on a specific view ('history' | 'active').
  // Set by the Home wallet (學分卡) so it lands on 已完成記錄, not 進行中.
  // Cleared by RecordScreen once consumed (onInitialRecordViewHandled).
  const [initialRecordView, setInitialRecordView] = React.useState(null);

  // Tab change.
  //   • Profile (我的) — no longer forces a sign-in overlay; guests see the
  //     profile shell with an in-page sign-in card (handled by ProfileScreen's
  //     guest variant). They can still browse Appearance / Preferences.
  //   • Record  (我的記錄) — its own top-level tab for everyone. Guests see
  //     the intro + sample preview; signed-in users see their timeline +
  //     enrolled courses. No longer redirects into Profile.
  const handleTabChange = React.useCallback((newTab, section) => {
    // Profile sub-section routing. An explicit `section` deep-links into it
    // (e.g. the Home bell → notificationCenter, a tapped reminder → record).
    // A plain Profile tab tap (no section) ALWAYS lands on the overview —
    // without this reset, profileSection stayed pinned to the last deep
    // section, so after opening the notification center from Home, every
    // later 個人 tap re-opened it (profileSection is re-applied on remount).
    if (newTab === 'profile') setProfileSection(section || 'overview');
    if (newTab === 'record' && section) setInitialRecordView(section);
    // Closing any open course detail when the user switches tabs — otherwise
    // the detail layer keeps overlaying the new tab content and it looks
    // like the nav did nothing.
    setOpenCourseId(null);
    setOpenProviderCode(null);
    setTab(newTab);
  }, []);

  const openNotificationCenter = React.useCallback(() => {
    if (!session) { requireAuth(() => setNotificationCenterOpen(true), 'default'); return; }
    setNotificationCenterOpen(true);
  }, [session, requireAuth]);
  const closeNotificationCenter = React.useCallback(() => setNotificationCenterOpen(false), []);

  // Home wallet (學分卡) → Record tab, opened on the completed-history view.
  const openRecordHistory = React.useCallback(() => {
    setInitialRecordView('history');
    handleTabChange('record');
  }, [handleTabChange]);

  // Single entry point for "edit my profile", shared by the Home greeting and
  // the Profile identity card. It routes by completion state so both surfaces
  // behave identically: a not-yet-onboarded user is sent through OnboardingFlow
  // to finish setup first; an onboarded user lands on the Edit Profile sub-page.
  // `gapId` is OPTIONAL and names the blank to land on — the same ids
  // auditReadiness emits ('name' | 'regNo' | 'cycle'), consumed by
  // EditProfilePage's focus effect. Home's route card passes 'cycle' so a nurse
  // with no certificate dates arrives at the field that fixes her whole cycle
  // rather than at the top of a long form.
  //
  // ‼️ A SyntheticEvent IS NOT A GAP ID. This callback is wired straight onto
  // onClick in three places (Home's greeting, Home's setup card, Profile's row),
  // so the first argument is usually a React event object. Storing one as the
  // focus target would make EditProfilePage scroll to the wrong section and
  // never clear — hence the typeof check rather than a truthiness test.
  const goToProfileEdit = React.useCallback((gapId) => {
    if (!session) return;
    if (!profile?.onboarded) {
      // Mirror the Profile card's onCompleteProfile path: clear the skip flag
      // so this is an intentional re-entry, reset deep state, open onboarding.
      // No focus target: onboarding asks for the dates itself, and leaving one
      // armed would fire on some unrelated later visit to Edit profile.
      setProfileEditTarget(null);
      AuthAPI.clearOnboardingSkipped?.(session.user.id);
      setTab('home');
      setAuthState('onboarding');
      return;
    }
    setProfileEditTarget(typeof gapId === 'string' ? gapId : null);
    handleTabChange('profile', 'editProfile');
  }, [session, profile?.onboarded, handleTabChange]);

  const resolveProfileReadinessGap = React.useCallback((gapId) => {
    if (!session) return;
    setProfileEditTarget(gapId || null);
    handleTabChange('profile', 'editProfile');
  }, [session, handleTabChange]);

  // Route a tapped reminder to the page/section that lets the user act on it:
  //   • enrolled  → the course detail overlay
  //   • cert      → Record tab, with that record's detail sheet opened so the
  //                 certificate can be uploaded straight away
  //   • renewal   → Record tab (cycle progress + log CTA live there)
  const routeReminder = React.useCallback((rm) => {
    if (!rm) return;
    if (rm.kind === 'enrolled' && rm.courseId) {
      setOpenProviderCode(null);
      setOpenCourseId(rm.courseId);
      return;
    }
    if (rm.kind === 'cert') {
      setOpenCourseId(null);
      setOpenProviderCode(null);
      setTab('record');
      if (rm.recordId) setFocusRecordId(rm.recordId);
      return;
    }
    if (rm.kind === 'renewal') {
      setOpenCourseId(null);
      setOpenProviderCode(null);
      setTab('record');
      return;
    }
    if (rm.kind === 'expired') {
      // Straight to the rollover sheet for that licence — the reminder's whole
      // point is the one-tap cycle update.
      setOpenCourseId(null);
      setOpenProviderCode(null);
      setTab('home');
      setSheet('rollover:' + (rm.licKey || 'primary'));
      return;
    }
    // Unknown kind — fall back to course detail when we have an id.
    if (rm.courseId) setOpenCourseId(rm.courseId);
  }, []);

  // Map a tapped push notification's payload (payload.kind + ids, set by
  // notify-cron / the admin functions) to the screen that lets the user act on
  // it. Reuses routeReminder for the course/record routes and handleTabChange
  // for Profile sub-sections (contributions / admin).
  const routeNotification = React.useCallback((data) => {
    if (!data) return;
    setNotificationCenterOpen(false);
    const courseId = data.courseId ? String(data.courseId) : null;
    switch (data.kind) {
      case 'renewal_reminder':
        routeReminder({ kind: 'renewal' });
        break;
      case 'date_hygiene':
        // "Confirm your certificate dates" → Edit Profile, where the hygiene
        // banner + date editors live.
        handleTabChange('profile', 'editProfile');
        break;
      case 'rollover_nudge':
        // "Renewed?" → straight into the rollover sheet for that licence.
        routeReminder({ kind: 'expired', licKey: data.license === 'secondary' ? 'secondary' : 'primary' });
        break;
      case 'cycle_start':
        // New cycle began — Record tab shows the fresh cycle's progress.
        routeReminder({ kind: 'renewal' });
        break;
      case 'class_reminder':
      case 'saved_course_deadline':
      // Seat alerts ride the same route: both are "something changed about a
      // course you saved", and the useful destination is the course itself.
      case 'seat_alert':
        if (courseId) routeReminder({ kind: 'enrolled', courseId });
        else handleTabChange('record');
        break;
      case 'cert_expiry':
        // Open the expiring record so renewing (or correcting the date) is one
        // tap away. routeReminder's existing 'cert' branch already switches to
        // the Record tab and sets focusRecordId.
        if (data.qualificationId) {
          setFocusQualificationId(String(data.qualificationId));
          handleTabChange('record', 'qualifications');
        } else if (data.recordId) routeReminder({ kind: 'cert', recordId: data.recordId });
        else handleTabChange('record', 'qualifications');
        break;
      case 'course_rule':
        // Straight to the course that matched — the whole point of the alert is
        // "look at this one", so anything else is an extra tap.
        if (courseId) setOpenCourseId(courseId);
        else handleTabChange('discover');
        break;
      case 'cne_pace':
        // Home is the right destination: the route card and the wallet both live
        // there, so "you are behind" lands next to "here is how to catch up".
        // Sending her to Discover instead would drop her into an unfiltered
        // catalogue with the question unanswered.
        handleTabChange('home');
        break;
      case 'missing_record': {
        // Best: drop straight into the log composer for that course. The
        // composer renders by looking the course up in the searchable set
        // (active ∪ archived), so only do that when it's loaded there;
        // otherwise fall back to course detail (which self-fetches) — robust
        // on a cold-start tap. Mirror the composer's pool so an archived
        // course (separate ARCHIVED_COURSES pool) reaches the composer too.
        const pool = (typeof allSearchableCourses === 'function')
          ? allSearchableCourses()
          : (typeof COURSES !== 'undefined' && Array.isArray(COURSES) ? COURSES : []);
        const loaded = courseId && pool.some((c) => String(c.id) === courseId);
        if (loaded) setLogCourseId(courseId);
        else if (courseId) routeReminder({ kind: 'enrolled', courseId });
        else handleTabChange('record');
        break;
      }
      case 'regulatory':
        // A rules change is only actionable once the nurse can see what it
        // means for HER cycle — that is the CNE information centre.
        handleTabChange('profile', 'cneInfo');
        break;
      case 'digest':
        setNotificationCenterOpen(true);
        break;
      case 'admin_submission':
      case 'admin_report':
      case 'admin_feedback':
        handleTabChange('profile', 'admin');
        break;
      case 'submission_approved':
      case 'submission_rejected':
      case 'report_accepted':
      case 'report_rejected':
      case 'report_applied':
      case 'feedback_reply':
        handleTabChange('profile', 'contributions');
        break;
      default:
        if (courseId) routeReminder({ kind: 'enrolled', courseId });
    }
  }, [routeReminder, handleTabChange]);

  // Warm taps: db.js dispatches 'vital:notification-tap'. Consume the stash too
  // so the cold-start drain below can't re-route the same tap.
  React.useEffect(() => {
    const onTap = (e) => {
      window.PushAPI?.consumePendingTap?.();
      routeNotification(e?.detail);
    };
    window.addEventListener('vital:notification-tap', onTap);
    return () => window.removeEventListener('vital:notification-tap', onTap);
  }, [routeNotification]);

  // AI-scan earn menu (LogSheet, 0 credits) → route to a way to earn:
  //   submit  → Discover + open the Submit-course sheet
  //   correct → Discover (browse to a course, tap ✎ per field) + a hint toast
  // (share is handled in-place inside LogSheet — no navigation.)
  React.useEffect(() => {
    const onEarn = (e) => {
      const method = e?.detail?.method;
      setSheet(null);
      setLogCourseId(null);
      handleTabChange('discover');
      if (method === 'submit') setDiscoverSubmitSignal((n) => n + 1);
      else if (method === 'correct') {
        window.showToast?.(L(STR.aiEarnCorrectHint, lang), { tone: 'info' });
      }
    };
    window.addEventListener('vital:earn-credit', onEarn);
    return () => window.removeEventListener('vital:earn-credit', onEarn);
  }, [handleTabChange, lang]);

  // Cold start: a tap that launched the app fires before the listener above is
  // attached, so db.js stashed it. Drain it once the session is ready and there
  // is somewhere to route.
  React.useEffect(() => {
    if (authState !== 'ready' || !session) return;
    const pending = window.PushAPI?.consumePendingTap?.();
    if (pending) routeNotification(pending);
  }, [authState, session, routeNotification]);

  // Upload a certificate file → Supabase storage, then refresh records.
  const uploadCertForRecord = React.useCallback(async (recordId, file) => {
    if (!session) return { error: { message: 'Not signed in' } };
    try {
      const res = await DB.uploadCertificate(session.user.id, recordId, file);
      if (res?.error) return { error: res.error };
      await reloadUserData();
      return res;
    } catch (e) {
      return { error: e };
    }
  }, [session, reloadUserData]);

  // Edit / delete a record from the RecordDetailSheet.
  const updateRecord = React.useCallback(async (recordId, patch) => {
    if (!session) return { error: { message: 'Not signed in' } };
    const res = await DB.updateRecord(recordId, patch);
    await reloadUserData();
    return res;
  }, [session, reloadUserData]);
  const deleteRecord = React.useCallback(async (recordId) => {
    if (!session) return { error: { message: 'Not signed in' } };
    const res = await DB.deleteRecord(recordId);
    await reloadUserData();
    return res;
  }, [session, reloadUserData]);

  // Sheet open — saved, reminders, log all require auth.
  const handleOpenSheet = React.useCallback((s, options) => {
    if (!session && (s === 'saved' || s === 'reminders' || s === 'log' || s === 'log:ai')) {
      requireAuth(() => setSheet(s), s);
      return;
    }
    if (s === 'pro:renewal' || s === 'pro:route') {
      const snapshot = options?.initialEnt;
      setProSheetInitialEnt(snapshot?.loading === false && session?.user?.id
        ? { userId: session.user.id, ent: snapshot }
        : null);
    }
    setSheet(s);
  }, [session, requireAuth]);

  // ── Vital Pro: "take me there" ─────────────────────────────────────────
  //
  // The five Pro benefits live across different screens, and two of them are
  // several taps deep. paywall.jsx names each one with a stable `nav` key and
  // knows nothing about tabs or sheets; this is the only place that key becomes
  // an actual navigation. Add a benefit → add its case here, or the row simply
  // renders no button (ProBenefitRows tolerates an unknown key by design).
  //
  // ‼️ NAVIGATION IS NOT ENTITLEMENT. Nothing here grants anything: every
  // destination re-reads the server entitlement through useProEntitlement, so a
  // stale or spoofed nav key lands a non-subscriber on a screen that still gates
  // her exactly as before. Never let a destination trust "I was navigated here".
  const goToProFeature = React.useCallback((key, target) => {
    setSheet(null);                 // the Pro sheet asked for this — close it
    setOpenCourseId(null);
    setOpenProviderCode(null);
    if (key === 'route') {
      if (target === 'setup') { setSheet('pro:setup'); return; }
      if (target === 'profile') { handleTabChange('profile', 'editProfile'); return; }
      setProFeatureTarget({ key: 'route', target: 'route', nonce: Date.now() });
      handleTabChange('home');
      return;
    }
    if (key === 'scan') { handleOpenSheet('log:ai'); return; }
    if (key === 'audit') {
      // The audit-pack toggle lives inside the NCHK export sheet, which is
      // ProfileScreen's own state. A nonce (not a boolean) so a second "take me
      // there" re-opens it after she has closed it once — the same one-shot
      // signal pattern as discoverSubmitSignal.
      handleTabChange('profile');
      setProFeatureTarget({ key: 'audit', target: 'audit', nonce: Date.now() });
      setProfileNchkSignal(n => n + 1);
      return;
    }
    // Course watches live in the same Notifications screen as the reminders —
    // one destination, two sections. If they ever split, split this too.
    if (key === 'reminders' || key === 'watch') {
      setProFeatureTarget({ key, target: target || key, nonce: Date.now() });
      handleTabChange('profile', 'notifications');
      return;
    }
  }, [handleTabChange, handleOpenSheet]);

  // ── "Have you started this cycle?" — the setup sheet's last step ────────
  //
  // Which certificate the question is about, and whether it is worth asking.
  // Both come from `cnePlanLicence` (data.js) — the SAME rule HomeScreen's
  // route card uses to pick which certificate to plan for. ‼️ Do not re-derive
  // it here: the answer is stored per licence key, so a second copy of the rule
  // that drifts would file the answer under a key the card never reads, and she
  // would be asked again forever with no way to tell why.
  //
  // Worth asking only when nothing is logged in that certificate's CNE window
  // and she has not already answered for this cycle.
  const progressAsk = React.useMemo(() => {
    if (!session || !profile) return null;
    const u = window.userFromProfile?.(profile, records);
    const lic = window.cnePlanLicence?.(window.cnePlanLicences?.(u) || []);
    if (!lic?.cycleStart) return null;
    const answered = profile?.prefs?.cneNotStarted?.[lic.key || 'primary'];
    if (answered === lic.cycleStart) return null;
    const inCycle = window.cycleRecords?.(records, lic.cycleStart, lic.renewalDate) || [];
    return inCycle.length ? null : lic;
  }, [session, profile, records]);

  // Store the "not started" answer. Per licence, per cycle — a rollover re-asks
  // rather than carrying a stale answer into a new window.
  //
  // ‼️ THE ONLY QUANTITY THIS MAY EVER RECORD IS ZERO. It adds no points, no
  // Annex A rows and no baseline, so the NCHK export is byte-identical either
  // way. A self-reported NUMBER would be an unevidenced baseline inside a
  // compliance document — see the ProSetupSheet header and onboarding.jsx:151.
  const markCycleNotStarted = React.useCallback(async () => {
    const uid = session?.user?.id;
    const lic = progressAsk;
    if (!uid || !lic?.cycleStart || !DB?.setPrefs) return;
    const next = {
      ...(profile?.prefs?.cneNotStarted && typeof profile.prefs.cneNotStarted === 'object'
        ? profile.prefs.cneNotStarted : {}),
      [lic.key || 'primary']: lic.cycleStart,
    };
    const { data, error } = await DB.setPrefs(uid, { cneNotStarted: next });
    // Lift the row so the route card re-renders from the stored answer rather
    // than from a second copy of it held here.
    if (!error && data) setProfile(data);
  }, [session, profile, progressAsk]);

  // Straight after a SUCCESSFUL PURCHASE (paywall.jsx dispatches this once the
  // SERVER has confirmed the entitlement — never on restore), open the
  // dedicated activation page. It replaces the completed purchase sheet rather
  // than stacking inside it, and it verifies the entitlement again on mount.
  //
  // ‼️ WHY AN EVENT AND NOT A PROP. Five call sites open the purchase sheet and
  // only two had wired a post-purchase handler, so three of them left a nurse
  // who had just been charged looking at an unchanged screen. A listener here
  // covers every entry point, including ones added later.
  React.useEffect(() => {
    const uid = session?.user?.id;
    const onPurchased = () => {
      if (!uid) return;
      // Defence in depth: purchase callers already close themselves before
      // dispatching, but the global owner also clears any sheet so the success
      // page can never share the modal stack with a stale purchase surface.
      setSheet(null);
      setProSheetInitialEnt(null);
      setProSuccessUserId(uid);
    };
    window.addEventListener('vital:pro-purchased', onPurchased);
    return () => window.removeEventListener('vital:pro-purchased', onPurchased);
  }, [session?.user?.id]);

  // Never carry a purchase moment across sign-out or into another account.
  React.useEffect(() => {
    const uid = session?.user?.id;
    setProSuccessUserId((current) => (!uid || (current && current !== uid)) ? null : current);
  }, [session?.user?.id]);

  // Saved is a Discover view, not an account/settings destination. The auth
  // gate resumes into the same segment after sign-in.
  const openSavedInDiscover = React.useCallback(() => {
    requireAuth(() => {
      handleTabChange('discover');
      setDiscoverSavedSignal(n => n + 1);
    }, 'saved');
  }, [requireAuth, handleTabChange]);

  // Free CNE card → Discover with the Free chip already on.
  //
  // NOT auth-gated, unlike Saved: browsing the catalogue needs no account, and
  // making a guest sign in to look at free courses would defeat the point of
  // the card. A FRESH object is built on every tap on purpose — DiscoverScreen's
  // inbound-filter effect keys on the object identity, so this is what lets a
  // second tap re-apply the chip after the user has cleared it herself.
  const openFreeCneInDiscover = React.useCallback(() => {
    setDiscoverInitFilter({ free: true });
    handleTabChange('discover');
  }, [handleTabChange]);

  // Log a finished (ended) course straight from its detail screen.
  // Auth-gated like the other record writes; the LogSheet opens prefilled
  // from the catalog course via initialCourse.
  const openLogForCourse = React.useCallback((id) => {
    requireAuth(() => setLogCourseId(id), 'log');
  }, [requireAuth]);

  // ── Deep-link navigation consumption ───────────────────────────────────
  // Applies a pending vital://<target> nav link stashed by handleDeepLink
  // (bootstrap effect). Runs when authState flips to 'ready' (cold start,
  // post-unlock, post-onboarding) and on navNonce bumps (warm taps while
  // already ready). Consumed exactly once; signed-out users go through the
  // requireAuth overlay inside handleOpenSheet and the action resumes after
  // sign-in. `home` is also handled for warm taps from another tab.
  React.useEffect(() => {
    if (authState !== 'ready' || !pendingNavRef.current) return;
    const { target, params } = pendingNavRef.current;
    pendingNavRef.current = null;
    if (target === 'log') handleOpenSheet(params?.scan === '1' ? 'log:ai' : 'log');
    else if (target === 'home') handleTabChange('home');
    else if (target === 'saved') openSavedInDiscover();
    else if (target === 'discover' || target === 'record') setTab(target);
    else if (target === 'profile') handleTabChange('profile');
    else if (target === 'course' && params?.id) setOpenCourseId(params.id);
    else if (target === 'group') { if (params?.code) setWardGroupJoinCode(String(params.code).toUpperCase()); handleTabChange('wardgroup'); }
    // 資格錢包 — the Record tab's qualifications view. Same destination the
    // cert_expiry reminder uses, so there is one way into the wallet.
    else if (target === 'wallet') handleTabChange('record', 'qualifications');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, navNonce]);

  const openSignIn = React.useCallback(() => {
    setAuthOverlay({ intent: 'signin' });
  }, []);

  // ── Routing ───────────────────────────────────────────────────────────
  // First-launch language picker. Renders BEFORE every other route on
  // the very first cold launch on a fresh install. Once the user picks
  // (or has picked any time previously), the localStorage marker
  // suppresses it forever. The chosen language goes into the existing
  // tweaks pipeline so every downstream lang/setLang reference picks
  // it up without any extra wiring.
  // Account deletion in progress — takes priority over every other
  // route so a mid-flight cascade can never fall through to the normal
  // app UI (which the user must not be able to touch while their data
  // is being wiped). Cleared by completeAccountDeletion() on success
  // or by the deleteAccount error branches on failure.
  if (deleting) {
    return <VitalSplash label={lang === 'zh' ? '刪除中…' : 'Deleting…'} />;
  }
  if (!langPicked) {
    return <LanguagePickerScreen onPick={(choice) => {
      try { localStorage.setItem('vital_lang_picked', '1'); } catch (_) {}
      setTweak('lang', choice);
      setLangPicked(true);
    }} />;
  }
  if (authState === 'loading') {
    return <VitalSplash label={lang === 'zh' ? '載入中' : 'Loading'} />;
  }
  if (authState === 'locked') {
    return <LockedScreen
      lang={lang} setLang={setLang}
      onRetry={attemptBiometricUnlock}
      onEscape={signInAnotherWay}
    />;
  }
  if (authState === 'offlineBrowse') {
    return <OfflineCatalogueScreen lang={lang} setLang={setLang} />;
  }
  if (authState === 'recoveryError') {
    return <RecoveryLinkErrorScreen
      lang={lang} setLang={setLang}
      error={recoveryErrorRef.current}
      onDone={() => {
        recoveryErrorRef.current = null;
        recoveryRef.current = false;
        postResetRef.current = false;
        if (window.location.hash) window.location.hash = '';
        setAuthState('ready');
      }}
    />;
  }
  if (authState === 'reset') {
    return <ResetPasswordScreen lang={lang} setLang={setLang} onDone={async () => {
      // After a successful password update we deliberately do NOT keep
      // the recovery session — the user must sign in fresh with the new
      // password. Order matters here:
      //   1. Set postResetRef BEFORE signOut so finalize() routes the
      //      signOut-triggered onAuthChange to 'postReset', not 'ready'.
      //   2. Clear recoveryRef so the same finalize() doesn't pick the
      //      higher-priority 'reset' branch and trap the user.
      //   3. Commit the state transition.
      //   4. Sign out; best-effort, swallow errors.
      postResetRef.current = true;
      recoveryRef.current = false;
      if (window.location.hash) window.location.hash = '';
      setAuthState('postReset');
      try { await window.sb.auth.signOut(); } catch (e) { /* best-effort */ }
    }} />;
  }
  if (authState === 'postReset') {
    return <PasswordResetSuccessScreen lang={lang} setLang={setLang} onDone={() => {
      postResetRef.current = false;
      setAuthState('ready');
      // Open the sign-in overlay so the user can enter their new password
      // immediately. The main shell renders behind it in guest mode.
      setAuthOverlay({ intent: 'signin' });
    }} />;
  }
  if (authState === 'onboarding' && session) {
    return <OnboardingFlow session={session} profile={profile} lang={lang} setLang={setLang}
      onDone={async ({ firstAction = 'later' } = {}) => {
        const p = await DB.getProfile(session.user.id);
        setProfile(p);
        try { AuthAPI.rememberIdentity?.({ email: session.user.email, name: p?.display_name }); } catch (_) {}
        // Persist the onboarded flag so a future offline cold launch
        // (Airplane Mode + biometric) routes to home rather than back
        // through onboarding. Also clear the "skipped" flag because the
        // two are mutually exclusive — completing onboarding supersedes
        // any earlier Skip.
        if (p?.onboarded) AuthAPI.markUserOnboarded(session.user.id);
        AuthAPI.clearOnboardingSkipped?.(session.user.id);
        // A nurse who just went through onboarding has already seen the
        // multiple-certificate capability, so suppress the one-time "what's new"
        // announcement for them (it's for pre-feature users only).
        try { localStorage.setItem('vital_whatsnew_dual_license', '1'); } catch (_) {}
        // Route the chosen first action. 'record' arms the deferred scan sheet
        // (see the effect that waits out biometric/other overlays); 'browse'
        // just lands them on Discover — a tab switch, never a modal, so it
        // cannot collide with the overlay sequencing that sheet has to respect.
        pendingFirstScanRef.current = firstAction === 'record';
        if (firstAction === 'browse') handleTabChange('discover');
        setAuthState('ready');
      }}
      onSkip={() => {
        // User chose "Skip for now" on the Welcome step. Persist the
        // per-user flag so subsequent bootstraps don't re-route them
        // back to onboarding. They can finish later via
        // Profile → Complete profile.
        AuthAPI.markOnboardingSkipped?.(session.user.id);
        try { localStorage.setItem('vital_whatsnew_dual_license', '1'); } catch (_) {}
        setAuthState('ready');
      }} />;
  }

  // ── Main app shell (used for both guest and signed-in) ────────────────
  const sideNavProps = {
    active: tab,
    lang, setLang,
    onLog: () => handleOpenSheet('log'),
    onOpenSheet: handleOpenSheet,
    profile, session,
    onSignIn: openSignIn,
    onSignOut: signOut,
    hasReminders: allReminders.length || 0,
  };

  // The tab screens stay mounted whether or not a course detail is open;
  // the detail is laid over the top of them via CSS. Keeping the underlying
  // tab in the DOM is what preserves the user's scroll position when they
  // hit "back" from a course detail — no manual scroll save/restore needed.
  const detailOpen = !!openCourseId;
  const proSuccessActive = !!session?.user?.id && proSuccessUserId === session.user.id;

  return (
    <React.Fragment>
    <div className="app"
      aria-hidden={proSuccessActive ? true : undefined}
      {...(proSuccessActive ? { inert: '' } : {})}>
      {showOfflineBanner && (
        <div className="offline-banner" role="status" aria-live="polite">
          <span>離線 — 顯示已快取的課程目錄 / Offline — showing cached course catalogue</span>
        </div>
      )}
      <SideNav {...sideNavProps} onChange={handleTabChange} />
      <div className="app-main">
        <div className="page-stack">
          {/* Underlying tab page — always mounted, kept inert when detail is on top */}
          <div className={'page-layer page-layer--tab' + (detailOpen ? ' is-hidden' : '')}
               aria-hidden={detailOpen}
               {...(detailOpen ? { inert: '' } : {})}>
            {tab === 'home' && (
              <HomeScreen lang={lang} setLang={setLang}
                onOpenCourse={setOpenCourseId}
                onTab={handleTabChange}
                onOpenNotifications={openNotificationCenter}
                onOpenRecordHistory={openRecordHistory}
                onOpenSheet={handleOpenSheet}
                onSignIn={openSignIn}
                savedIds={savedIds}
                enrolledIds={enrolledIds}
                session={session}
                profile={profile}
                records={records}
                reminders={allReminders}
                onRouteReminder={routeReminder}
                onEditProfile={session ? goToProfileEdit : undefined}
                onProfileChanged={(saved) => { if (saved) setProfile(saved); }}
                onOpenFreeCne={openFreeCneInDiscover}
                proFeatureTarget={proFeatureTarget?.key === 'route' ? proFeatureTarget : null}
                onProFeatureTargetConsumed={() => setProFeatureTarget(null)}
              />
            )}
            {tab === 'discover' && (
              <DiscoverScreen lang={lang} setLang={setLang}
                onOpenCourse={setOpenCourseId}
                view={t.discoverView}
                setView={(v) => setTweak('discoverView', v)}
                initialFilters={discoverInitFilter}
                coursesVersion={coursesVersion}
                profile={profile}
                session={session}
                savedIds={savedIds}
                onOpenSaved={openSavedInDiscover}
                savedSignal={discoverSavedSignal}
                onSavedConsumed={() => setDiscoverSavedSignal(0)}
                submitSignal={discoverSubmitSignal}
                onSubmitConsumed={() => setDiscoverSubmitSignal(0)}
                onRequireAuth={requireAuth}
                onOpenContributions={() => handleTabChange('profile', 'contributions')}
              />
            )}
            {tab === 'providers' && !openProviderCode && (
              <ProvidersScreen lang={lang} setLang={setLang}
                providers={providers}
                providersLoading={providersLoading}
                onOpenProvider={setOpenProviderCode}
              />
            )}
            {tab === 'providers' && openProviderCode && (
              <ProviderCoursesScreen
                providerCode={openProviderCode}
                providers={providers}
                lang={lang} setLang={setLang}
                onBack={() => setOpenProviderCode(null)}
                onOpenCourse={(id) => { setOpenProviderCode(null); setOpenCourseId(id); }}
              />
            )}
            {tab === 'record' && (
              <RecordScreen
                lang={lang} setLang={setLang}
                session={session}
                /* The one-tap 值得上 ask at the completion moment
                   (ExperienceAskCard) writes a REACTION, so it needs the
                   auth-aware toggle rather than calling DB directly — a
                   signed-out tap must open the auth flow, not fail quietly. */
                toggleCourseReaction={toggleCourseReaction}
                profile={profile}
                records={records}
                qualificationsReady={qualificationsReady}
                walletItems={walletUniverse}
                licenceEvidence={licenceEvidence}
                licenceEvidenceReady={licenceEvidenceReady}
                onEvidenceChanged={reloadUserData}
                qualifications={/* ‼️ The wallet tab reads THIS array.
                    RecordScreen defaults it to `[]`, so omitting it does not
                    crash — it renders the empty-wallet state over real data,
                    which is exactly how "個人頁 1 項資格 → 我的資格 0 項"
                    happened. Any surface claiming a qualification count must
                    read the same array. */
                  qualifications}
                enrolledIds={enrolledIds}
                onSignIn={openSignIn}
                onLog={/* Keep the qualification-wallet scan contextual: it
                          opens the shared AI extractor, but its review saves
                          only to the credential wallet rather than CNE. */
                  (mode) => handleOpenSheet(mode === 'ai:qualification'
                    ? 'log:ai:qualification'
                    : mode === 'ai' ? 'log:ai' : 'log')}
                onOpenCourse={setOpenCourseId}
                onUploadCert={uploadCertForRecord}
                onUpdateRecord={updateRecord}
                onDeleteRecord={deleteRecord}
                onCreateRecord={addRecord}
                onToggleEnrolled={toggleEnrolled}
                focusRecordId={focusRecordId}
                onFocusRecordHandled={() => setFocusRecordId(null)}
                focusQualificationId={focusQualificationId}
                onFocusQualificationHandled={() => setFocusQualificationId(null)}
                initialView={initialRecordView}
                onInitialViewHandled={() => setInitialRecordView(null)}
                onEditProfile={resolveProfileReadinessGap}
                onOpenPro={() => setSheet('pro:renewal')}
                onSaveQualification={saveQualification}
                onUpdateQualification={updateQualification}
                onArchiveQualification={archiveQualification}
                onDeleteQualificationVersion={removeQualificationVersion}
              />
            )}
            {tab === 'profile' && (
              <ProfileScreen lang={lang} setLang={setLang}
                theme={t.theme} setTheme={(v) => setTweak('theme', v)}
                onOpenSheet={handleOpenSheet}
                onSignIn={openSignIn}
                session={session}
                profile={profile}
                records={records}
                qualifications={qualifications}
                qualificationsReady={qualificationsReady}
                walletItems={walletUniverse}
                enrolledIds={enrolledIds}
                onSignOut={signOut}
                onDeleteAccount={session ? () => setSheet('deleteAccount') : undefined}
                onProfileSaved={(saved) => {
                  // EditProfilePage already upserted; `saved` is the full
                  // updated row. Lift it into app state so Overview/Home
                  // reflect the change without a refetch, and keep the
                  // returning-user identity (name shown on the sign-in card)
                  // in sync. Fall back to a refetch if the row didn't come back.
                  if (saved) setProfile(saved);
                  else if (session) DB.getProfile(session.user.id).then(p => p && setProfile(p));
                  try { AuthAPI.rememberIdentity?.({ email: session?.user?.email, name: saved?.display_name }); } catch (_) {}
                }}
                onCompleteProfile={session && !profile?.onboarded
                  ? () => {
                      // Clear skipped flag so the user is intentionally
                      // re-entering onboarding from settings. Either they
                      // complete it (markUserOnboarded fires from
                      // OnboardingFlow.onDone) or they skip again (re-set
                      // by onSkip). Both transitions land on 'ready'.
                      AuthAPI.clearOnboardingSkipped?.(session.user.id);
                      setTab('home'); // reset any deep state
                      setAuthState('onboarding');
                    }
                  : undefined}
                initialSection={profileSection}
                onSectionChange={(next) => {
                  setProfileSection(next);
                  if (next === 'editProfile' && profileSection !== 'editProfile') setProfileEditTarget(null);
                }}
                editFocusTarget={profileEditTarget}
                onEditFocusTargetConsumed={() => setProfileEditTarget(null)}
                nchkSignal={profileNchkSignal}
                onNchkConsumed={() => setProfileNchkSignal(0)}
                proFeatureTarget={proFeatureTarget}
                onProFeatureTargetConsumed={() => setProFeatureTarget(null)}
                onNavigateProFeature={goToProFeature}
                onOpenNotifications={openNotificationCenter}
                savedIds={savedIds}
                onOpenCourse={setOpenCourseId}
                onTab={handleTabChange}
                onUploadCert={uploadCertForRecord}
              />
            )}

            {tab === 'wardgroup' && (
              <WardGroupScreen
                lang={lang}
                session={session}
                profile={profile}
                onSignIn={openSignIn}
                pendingJoinCode={wardGroupJoinCode}
                onJoinCodeHandled={() => setWardGroupJoinCode(null)}
                onProfileChanged={(p) => setProfile(p)}
              />
            )}
          </div>

          {/* Course detail — overlays the tab layer when a course is open */}
          {detailOpen && (
            <div className="page-layer page-layer--detail">
              <CourseDetailScreen
                courseId={openCourseId}
                lang={lang}
                savedIds={savedIds}
                toggleSaved={toggleSaved}
                enrolledIds={enrolledIds}
                toggleEnrolled={toggleEnrolled}
                records={records}
                onLogCourse={openLogForCourse}
                onBack={() => setOpenCourseId(null)}
                session={session}
                profile={profile}
                toggleCourseReaction={toggleCourseReaction}
              />
            </div>
          )}
        </div>

        {!(tab === 'profile' && profileSection === 'editProfile') && (
          <TabBar
            active={tab}
            onChange={handleTabChange}
            lang={lang}
            onLog={() => handleOpenSheet('log')}
            recordBadge={recordTabBadge}
          />
        )}
      </div>

      {sheet === 'log'       && <LogSheet lang={lang} profile={profile} onClose={() => setSheet(null)} onSave={addRecord} onSaveQualification={saveQualification} />}
      {sheet === 'log:ai'    && <LogSheet lang={lang} profile={profile} initialMode="ai" onClose={() => setSheet(null)} onSave={addRecord} onSaveQualification={saveQualification} />}
      {sheet === 'log:ai:qualification' && <LogSheet lang={lang} profile={profile} initialMode="ai" initialDestination="qualification" onClose={() => setSheet(null)} onSave={addRecord} onSaveQualification={saveQualification} />}
      {/* Log-from-detail — ended course recorded straight from its page.
          Opens on the manual form prefilled with that course. */}
      {logCourseId != null && (() => {
        // Resolve from the full searchable set (active ∪ archived), NOT just
        // COURSES: an ended course reached via "Show past courses" may live
        // in the separate ARCHIVED_COURSES pool. Looking it up in COURSES
        // alone returns undefined → the sheet silently never opens (the
        // "no response" tapping 記錄完成 on an archived course).
        const pool = (typeof allSearchableCourses === 'function')
          ? allSearchableCourses()
          : (typeof COURSES !== 'undefined' ? COURSES : []);
        const lc = pool.find(x => x.id === logCourseId);
        if (!lc) return null;
        // Low-confidence rows (rejected_low_confidence — or any unknown
        // non-active, non-archived lifecycle) get the verification gate:
        // a warning step + audit-critical fields blanked. Archived/active
        // ended courses log directly (their data was trusted when live).
        const ls = lc.lifecycleStatus;
        const needsVerification = !!(ls && ls !== 'active' && ls !== 'archived');
        return <LogSheet lang={lang} profile={profile} initialCourse={lc} needsVerification={needsVerification}
          onSaveQualification={saveQualification}
                         onClose={() => setLogCourseId(null)} onSave={addRecord} />;
      })()}
      {sheet === 'saved'     && <SavedSheet lang={lang} savedIds={savedIds} onOpenCourse={setOpenCourseId} onClose={() => setSheet(null)} />}
      {sheet === 'reminders' && <RemindersSheet lang={lang} reminders={allReminders} onClose={() => setSheet(null)} onOpenCourse={setOpenCourseId} onRouteReminder={routeReminder} />}
      {typeof sheet === 'string' && sheet.startsWith('rollover:') && session && profile && (
        <RolloverSheet
          lang={lang}
          session={session}
          profile={profile}
          records={records}
          licKey={sheet.slice('rollover:'.length) || 'primary'}
          onSaved={(saved) => { if (saved) setProfile(saved); }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'privacy'   && <PrivacySheet lang={lang} onClose={() => setSheet(null)} />}
      {/* ProSheet, not PaywallSheet — a subscriber who reaches this from the
          audit-readiness card gets her membership sheet, not a second price. */}
      {/* The global vital:pro-purchased listener opens the server-verified
          activation page. Individual paywall entry points therefore need no
          competing onPurchased transition. */}
      {sheet === 'pro:renewal' && typeof ProSheet === 'function' && (
        <ProSheet lang={lang} profile={profile} trigger="renewal" records={records}
          userId={session?.user?.id}
          initialEnt={proSheetInitialEnt?.userId === session?.user?.id ? proSheetInitialEnt.ent : null}
          onClose={() => { setSheet(null); setProSheetInitialEnt(null); }}
          onPersonalise={() => { setProSheetInitialEnt(null); setSheet('pro:setup'); }}
          onNavigate={goToProFeature} />
      )}
      {/* Opened only from HomeScreen's route card, which itself renders only
          when cneRoutePlan() produced a real route — so this entry point can
          never pitch a plan the catalogue cannot currently build. */}
      {sheet === 'pro:route' && typeof ProSheet === 'function' && (
        <ProSheet lang={lang} profile={profile} trigger="route" records={records}
          userId={session?.user?.id}
          initialEnt={proSheetInitialEnt?.userId === session?.user?.id ? proSheetInitialEnt.ent : null}
          onClose={() => { setSheet(null); setProSheetInitialEnt(null); }}
          onPersonalise={() => { setProSheetInitialEnt(null); setSheet('pro:setup'); }}
          onNavigate={goToProFeature} />
      )}
      {/* Optional profile/route questions remain reachable from the activation
          checklist, the membership sheet and the Home route card. Purchase
          never jumps straight into this form: the success page first explains
          what is ready and lets the nurse choose whether to continue. */}
      {sheet === 'pro:setup' && session && typeof ProSetupSheet === 'function' && (
        <ProSetupSheet key="pro-setup" lang={lang} profile={profile} userId={session.user.id}
          askProgress={!!progressAsk}
          onLogRecord={() => setSheet('log:ai')}
          onNotStarted={markCycleNotStarted}
          onClose={() => setSheet(null)}
          onSaved={(row) => { if (row) setProfile(row); }}
          onNavigate={goToProFeature} />
      )}
      {sheet === 'deleteAccount' && session && (
        <DeleteAccountSheet
          lang={lang}
          email={session.user.email}
          provider={AuthAPI.getSessionProvider(session)}
          onClose={() => setSheet(null)}
          onConfirm={deleteAccount}
        />
      )}

      {notificationCenterOpen && session && (
        <div className="page-layer page-layer--detail" role="dialog" aria-modal="true">
          <NotificationCenterScreen
            lang={lang}
            userId={session.user.id}
            onBack={closeNotificationCenter}
          />
        </div>
      )}

      {enrolPromptOpen && session && (
        <BiometricEnrolSheet
          lang={lang}
          biometryType={enrolBiometryType}
          onEnable={onEnrolEnable}
          onDecline={onEnrolDecline}
        />
      )}

      {pushSoftAsk && session && !enrolPromptOpen && (
        <PushSoftAskSheet
          lang={lang}
          moment={pushSoftAsk.moment}
          onEnable={async () => {
            // The soft-ask was the priming; now run the real two-stage flow.
            try { await window.PushAPI?.requestAndRegister?.(); } catch (_) {}
            setPushSoftAsk(null);
          }}
          onDismiss={() => setPushSoftAsk(null)}
        />
      )}

      {handleInvite && session && !pushSoftAsk && !enrolPromptOpen && !reviewAsk && (
        <HandleInviteSheet
          lang={lang}
          onSetHandle={() => { setHandleInvite(null); handleTabChange('profile', 'editProfile'); }}
          onDismiss={() => setHandleInvite(null)}
        />
      )}

      {whatsNewDual && session && !pushSoftAsk && !handleInvite && !enrolPromptOpen && !reviewAsk && (
        <WhatsNewDualLicenseSheet
          lang={lang}
          onGoToProfile={() => { dismissWhatsNewDual(); handleTabChange('profile', 'editProfile'); }}
          onDismiss={dismissWhatsNewDual}
        />
      )}

      {reviewAsk && session && !pushSoftAsk && !handleInvite && !enrolPromptOpen && !whatsNewDual && (
        <ReviewAskSheet
          lang={lang}
          onPositive={() => {
            // Keep the navigation in the original tap's call stack. Delaying it
            // loses the user gesture and requestReview() is allowed to show
            // nothing; this explicit action must always open the review page.
            if (typeof window.openAppStoreReview !== 'function') {
              console.error('[Vital review] App Store review link is unavailable');
              return; // never close the sheet while silently doing nothing
            }
            window.openAppStoreReview();
            setReviewAsk(null);
          }}
          onFeedback={() => { setReviewAsk(null); setReviewFeedbackOpen(true); }}
          onDismiss={() => setReviewAsk(null)}
        />
      )}

      {/* FeedbackSheet instance owned by the review triage — the Profile one is
          mounted inside ProfileScreen only, and the triage can fire on any tab. */}
      <FeedbackSheet open={reviewFeedbackOpen} onClose={() => setReviewFeedbackOpen(false)}
                     lang={lang} session={session} />

      {authOverlay && (
        <div className="auth-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeAuthOverlay(); }}>
          <AuthRouter
            lang={lang} setLang={setLang}
            onClose={closeAuthOverlay}
            initialMode={authOverlay.intent === 'signup' ? 'signup' : 'signin'}
          />
        </div>
      )}

      <TweaksPanel>
        <TweakSection label="Language" />
        <TweakRadio label="UI language" value={t.lang}
          options={[{ value: 'en', label: 'EN' }, { value: 'zh', label: '繁中' }]}
          onChange={(v) => setTweak('lang', v)} />
        <TweakSection label="Theme" />
        <TweakRadio label="Mode" value={t.theme}
          options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }, { value: 'auto', label: 'Auto' }]}
          onChange={(v) => setTweak('theme', v)} />
        <TweakSection label="Layout" />
        <TweakRadio label="Density" value={t.density}
          options={[{ value: 'compact', label: 'Compact' }, { value: 'regular', label: 'Regular' }, { value: 'comfy', label: 'Comfy' }]}
          onChange={(v) => setTweak('density', v)} />
        <TweakRadio label="Discover view" value={t.discoverView}
          options={[{ value: 'list', label: 'List' }, { value: 'grid', label: 'Grid' }]}
          onChange={(v) => setTweak('discoverView', v)} />
      </TweaksPanel>
    </div>
    {proSuccessActive && typeof ProActivationSuccessScreen === 'function' && (
      <ProActivationSuccessScreen
        lang={lang}
        userId={session.user.id}
        profile={profile}
        records={records}
        onLater={() => setProSuccessUserId(null)}
        onNavigate={(key, target) => {
          setProSuccessUserId(null);
          goToProFeature(key, target);
        }}
      />
    )}
    </React.Fragment>
  );
}

window.NurseCNEApp = NurseCNEApp;

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { settleWithin, PROFILE_LOAD_TIMEOUT_MS, NurseCNEApp });
